import { CommonDialogs } from "@mcleod/common";
import { BlurEvent, Button, ChangeEvent, ClickEvent, Component, ContextMenuCreationEvent, CrudDecorator, DataDisplayEvent, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, Dialog, DialogProps, DropdownItem, EditRowDecorator, Label, Layout, LayoutProvidedCrudDecoratorProps, LookupModelSearchEvent, LookupModelSelectionEvent, ModelDataChangeType, Panel, SaveButton, SlideoutDecorator, Snackbar, Switch, Tab, Table, TableAction, TableAddRowResult, TableContentsChangedEvent, TableRow, TableRowBeforeSaveEvent, TableRowDisplayEvent, TableRowMode, Textbox, Toast, ValidationResult } from "@mcleod/components";
import { ActionPermission, Alignment, Api, ArrayUtil, CurrencySettings, DisplayType, HorizontalAlignment, LogManager, McLeodClassicIntegration, Model, ModelRow, ModelRowType, Navigation, PermissionsUtil, StringUtil, SuccessFail, VerticalAlignment, WindowTitle } from "@mcleod/core";
import { AppointmentUtil, CheckDuplicateBOL, DuplicateBOLSource, ModelOrders, NextAction, OrderHandlingRequirementsSlideout, OrderTabView, PostToLoadBoards, RateUtil, RearrangeStopSlideout, RecurringOrderPrompt, VoidOrderPrompt } from "@mcleod/dispatch";
import { getDispatchControlBoolean, getDispatchControlValue, LMECompanySettings, LMEGeneralSettings, ResponsibilityUtil, UsersResponsibilitySlideoutTrans } from "@mcleod/general";
import { ImageName } from "@mcleod/images";
import { CompareHistoryList } from "./CompareHistoryList";
import { CompareTender } from "./CompareTender";
import { DataFusionLocationCrossReference } from "./DataFusionLocationCrossReference";
import { DeclineTender } from "./DeclineTender";
import { EdiOrderOverview } from "./EdiOrderOverview";
import { EdiOrderRates } from "./EdiOrderRates";
import { EdiProfileOrderQuickInfo } from "./EdiProfileOrderQuickInfo";
import { EdiRawDataSlideout } from "./EdiRawDataSlideout";
import { EdiStop } from "./EdiStop";
import { EdiStopProgress } from "./EdiStopProgress";
import { EdiSuggestedPricing } from "./EdiSuggestedPricing";
import { EdiUtility } from "./EdiUtility";
import { LinkTenderDialog } from "./LinkTenderDialog";
import { MakeOrderUtility } from "./MakeOrderUtility";
import { OrderLoadTenderHistory } from "./OrderLoadTenderHistory";
import { PurposeSwap } from "./PurposeSwap";
import { PurposeSwapDialog } from "./PurposeSwapDialog";
import { TransmissionStatusQuickInfo } from "./TransmissionStatusQuickInfo";
import { AutogenLayoutViewEdiOrder } from "./autogen/AutogenLayoutViewEdiOrder";
import { ModelEdiOrder, RowEdiOrder } from "./models/ModelEdiOrder";
import { ModelEdiorderProfile, RowEdiorderProfile } from "./models/ModelEdiorderProfile";
import { ModelOtherChargeEdi } from "./models/ModelOtherChargeEdi";

const log = LogManager.getLogger("lme.datafusion.ViewEdiOrder");

const equipmentTypeOptionsMap = {
    tarps: "T",
    expedited: "E"
};

export class ViewEdiOrder extends AutogenLayoutViewEdiOrder {
    private _doAfterAction: () => void = () => this.mainDataSource.search({ id: this.mainDataSource.activeRow.get("id") });
    private _nextAction: string | (() => void);
    private checkDuplicateBOL: CheckDuplicateBOL;
    private _invokedFromEdi: boolean = false;
    private loadingRecurringOrder: boolean = false;
    private _isBrokerageLtl: boolean = false;
    private _profile: RowEdiorderProfile;
    private _ediOrderRow: RowEdiOrder;
    private _updatingEquipmentType: boolean = false;

    override onLoad(): void {
        const dispatchControl = LMEGeneralSettings.getSingleton().getDispatchControl();
        const mPactConfigured = dispatchControl.target_pay_calc == "M" &&
            !StringUtil.isEmptyString(dispatchControl.mpact_smart_api_key) &&
            !StringUtil.isEmptyString(dispatchControl.mpact_host_url);

        this.textboxEquipmentTypeIdGeneral.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.userInitiatedChange && event.hasSelection() === true && !this._updatingEquipmentType) {
                this._updatingEquipmentType = true;
                this.textboxEquipmentTypeIdEquipment.text = this.textboxEquipmentTypeIdGeneral.text;
                if (mPactConfigured)
                    this.layoutRates.calculateRates(false, false);
                this._updatingEquipmentType = false;
            }
        });

        this.tabFA.visible = false;

        this.textboxEquipmentTypeIdEquipment.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.userInitiatedChange && event.hasSelection() === true && !this._updatingEquipmentType) {
                this._updatingEquipmentType = true;
                this.textboxEquipmentTypeIdGeneral.text = this.textboxEquipmentTypeIdEquipment.text;
                if (mPactConfigured)
                    this.layoutRates.calculateRates(false, false);
                this._updatingEquipmentType = false;
            }
        });

        this.textboxCommodityId.addChangeListener(event => {
            if (!event.userInitiatedChange)
                return;
            this.commodityCodeChanged();
        });

        this.textboxRevenueCodeId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (!event.userInitiatedChange)
                return;
            if (this.textboxRevenueCodeId.getFirstLookupModelData()?.get("id") != null)
                this.revenueCodeChanged(this.textboxRevenueCodeId.getFirstLookupModelData().get("id"));
        });

        this.textboxOrderType.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (!event.userInitiatedChange)
                return;
            if (this.textboxOrderType.getFirstLookupModelData()?.get("id") != null)
                this.orderTypeChanged(this.textboxOrderType.getFirstLookupModelData().get("id"));
        });

        this.textboxCustomerId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (!event.userInitiatedChange)
                return;
            const custId = this.textboxCustomerId.getFirstLookupModelData()?.get("id");
            if (custId != null) {
                (this.layoutOverview as EdiOrderOverview).customerChanged(this.textboxCustomerId.getFirstLookupModelData());
            }
        });

        const userfilter = (event: LookupModelSearchEvent) => event.filter.web_user_type = "U";
        this.textboxEnteredUserId.addBeforeLookupModelSearchListener(userfilter);

        if (history.state != null && history.state.state?.successAdd == true)
            Toast.showSuccessToast("Yay! Your order was created successfully!", "Order number " + history.state.state?.orderId);

        this.stopsTable.onEditLayoutLoaded = this.stopTableOnEditLoaded();

        this.stopsTable.allowDelete = false;
        this.mainDataSource.addAfterModeChangeListener(event => {
            if (this.mainDataSource.mode == DataSourceMode.SEARCH) {
                this.stopsTable.dataSource = null;
                this.textboxOrderedDate.displayType = DisplayType.DATERANGE;
                this.setTitle();
                this.dataheaderEdiOrder.syncTitle();
            }
            else if (this.stopsTable.dataSource == null)
                this.stopsTable.dataSource = this.sourceEdiStop;

            if (this.sourceViewEdiOrder.activeRow?.get("id") == null || this.sourceViewEdiOrder.activeRow?.get("id") == undefined) {
                this.buttonTenderRawData.visible = false;
                this.buttonReplyRawData.visible = false;
                this.tabErrors.visible = false;
            }

        });

        this.textboxOrderedBy.addBeforeLookupModelSearchListener(event => {
            event.filter.parent_row_id = this.mainDataSource.activeRow?.get("customer_id");
            event.filter.parent_row_type = "C";
        });

        this.textboxOrderedBy.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (!event.userInitiatedChange)
                return;
            if (event.hasSelection() === true)
                this.mainDataSource.activeRow.set("ordered_by_id", event.selection.get("id"))
        });

        this.layoutRates.checkPickupAndDeliveryEntered = () => {
            const stopData = this.sourceEdiStop.data;
            if (stopData.length < 2) return false;
            const pickupEntered = stopData[0].get("stop_type") === "PU";
            const deliveryEntered = stopData[stopData.length - 1].get("stop_type") === "SO";
            return pickupEntered && deliveryEntered;
        }

        this.setDispatchSettings();
        this.mainDataSource.componentValidationFailedCallback = (result: ValidationResult) => this.onComponentValidationFailed(result);
        this.sourceViewEdiOrder.afterGetDataboundValues = (row: ModelRow, dataSource: DataSource) => this.sourceOrdersAfterGetDataboundValues(row, dataSource);

        if (this.displayingFromToolbox === true) {
            this.dataHeader.showSaveAndClose = true;
            this.dataHeader.showSave = false;
        }
        this.styleTab(this.tabErrors);

        this.layoutOverview.imageInboundTenderError.addClickListener(event => {
            this.tabsetAddOrders.scrollToTab(this.tabTenderInfo);
            this.tabMapErrors.expandAll();
        });
        this.layoutOverview.imageOutboundTenderError.addClickListener(event => {
            this.tabsetAddOrders.scrollToTab(this.tabTenderInfo);
            this.tabMapErrors.expandAll();
        });
        this.layoutOverview.imageReplyError.addClickListener(event => {
            this.tabsetAddOrders.scrollToTab(this.tabReply);
            this.layoutReply.tabErrors.tabset.expandAll();
        });

        this.layoutOverview.imageInboundWarning.addClickListener(event => {
            this.tabsetAddOrders.scrollToTab(this.tabTenderInfo);
            this.tabMapErrors.expandAll();
        });

        this.labelOrderId.addDataDisplayListener(event => {
            const orderId = this.mainDataSource.activeRow?.get("order_id");
            const originalOrder = this.mainDataSource.activeRow?.get("original_order");
            if (!StringUtil.isEmptyString(orderId) && "Y" === originalOrder) {
                this.toggleEditFields(false);
                this.checkboxNoDisplay.enabled = true;
            }
        });

        this.layoutOverview.imageXRef.addClickListener(event => {
            this.createXref();
        });

        this.layoutOverview.imageCommitment.addClickListener(event => {
            this.imageCommitmentOnClick(event);
        });

        const partialShipmentsEnabled = "Y" == LMEGeneralSettings.getSingleton().getDispatchControl()["enable_manifesting"];
        this.textboxOrderLength.visible = partialShipmentsEnabled;

        this.setTransmissionStatusTooltipContent(this.labelTransmitStatus);
        this.setTransmissionStatusTooltipContent(this.textboxGs06GroupCntlno, true);
        this.setTransmissionStatusTooltipContent(this.textboxIsa13IntrCntlno, true);
        const orderMethodDropDownItems: DropdownItem[] = [
            new DropdownItem("E", "DataFusion"),
            new DropdownItem("M", "Manual"),
            new DropdownItem("W", "Web")
        ];
        this.textboxOrderedMethod.items = orderMethodDropDownItems;
        this.setupLabelPartnerNameTooltipContent();

    }
    setupLabelPartnerNameTooltipContent() {
        this.textboxPartnerId.tooltipDynamicContent = async () => {
            const row = this.sourceViewEdiOrder.activeRow;
            const partnerID = row?.get("partner_id");
            const version = row?.get("version");
            const direction = row?.get("direction");
            if (partnerID != null && version != null && direction != null) {
                const batch = row?.get("gs06_group_cntlno");
                const createDate = row?.get("create_date");
                const layout = await Layout.getLoadedLayout("lme/datafusion/EdiProfileOrderQuickInfo") as EdiProfileOrderQuickInfo;
                await layout.sourceEdiorderProfile?.search({
                    partner_id: partnerID,
                    version,
                    direction,
                    batch,
                    trans_type: "T",
                    create_date: createDate
                });
                return layout;
            }
            return null;
        };
    }
    async textboxOrderLengthOnBlur(event: BlurEvent) {
        const textboxOrderLength: Textbox = event.target as Textbox;
        const orderLength = textboxOrderLength.text;
        if (!StringUtil.isEmptyString(orderLength)) {
            const parsedValue = parseFloat(orderLength);
            if (isNaN(parsedValue) || parsedValue <= 0) {
                this.activeRow.set({ order_length: null });
                await Snackbar.showWarningSnackbar('Order length must be greater than zero.');
                textboxOrderLength.focus();
            }
        }
    }

    onDirectionDisplay(event: DataDisplayEvent) {
        const isOrderIdEmpty = StringUtil.isEmptyString(this.mainDataSource.activeRow?.get("order_id"));

        if (this.mainDataSource.activeRow?.get("direction") != "O") {
            const multiCurrencyEnabled = CurrencySettings.getSingleton().multiCurrencyEnabled();
            this.panelOutbound.visible = false;
            this.textboxCustomerId.enabled = isOrderIdEmpty || multiCurrencyEnabled;
            this.layoutRates.buttonRate.enabled = isOrderIdEmpty;
            this.checkboxNoDisplay.visible = true;

        }
        else {
            this.textboxCustomerId.enabled = false;
            this.layoutRates.buttonRate.enabled = false;
            this.checkboxNoDisplay.visible = false;
            this.checkboxNoDisplay.enabled = false;
        }
    }

    override get windowTitle(): string {
        if (this.sourceViewEdiOrder.activeRow?.get("id") != null && this.sourceViewEdiOrder.activeRow?.get("id") != undefined)
            return "View Tender " + this.sourceViewEdiOrder.activeRow?.get("id");
        return "Search Tenders";
    }

    async setTitle() {
        const orderId = this.sourceViewEdiOrder.activeRow?.get("order_id");
        const tenderId = this.sourceViewEdiOrder.activeRow?.get("id");
        const recordInUse = this.sourceViewEdiOrder.activeRow?.get("record_locked");
        const overview = this.layoutOverview as EdiOrderOverview;
        this.buttonUsers.visible = await ResponsibilityUtil.isFilterEnabled("edi_filter");


        if (orderId != null) {
            this.labelOrderNumber.visible = true;
            this.labelOrderId.visible = true;
            this.imageGoToOrder.visible = true;
            this.labelOrderStatus.visible = true;
            const orderStatus = this.sourceViewEdiOrder.activeRow?.get("order_status");

            if (orderStatus == "A") {
                this.labelOrderStatus.text = "Available";
            } else if (orderStatus == "D") {
                this.labelOrderStatus.text = "Delivered";
            } else if (orderStatus == "V") {
                this.labelOrderStatus.text = "Void";
            } else if (orderStatus == "P") {
                this.labelOrderStatus.text = "In Progress";
            }

            const moveStatus = this.sourceViewEdiOrder.activeRow?.get("move_status");
            if (moveStatus == "A") {
                overview.labelMoveStatus.text = "Available";
            } else if (moveStatus == "D") {
                overview.labelMoveStatus.text = "Delivered";
            } else if (moveStatus == "V") {
                overview.labelMoveStatus.text = "Void";
            } else if (moveStatus == "P") {
                overview.labelMoveStatus.text = "In Progress";
            }

        }
        else {
            this.labelOrderNumber.visible = false;
            this.labelOrderId.visible = false;
            this.imageGoToOrder.visible = false;
            this.labelOrderStatus.visible = false;
            overview.labelMoveStatus.visible = false;
            overview.moveLabel.visible = false;
            overview.buttonMoveDetails.visible = false;
        }

        if (tenderId != null) {
            this.labelTenderNumber.visible = true;
            this.labelLabel5.visible = true;
            this.buttonLock.visible = true;
            this.classicButton.visible = true;
            this.labelAdvSearch.visible = false;
        }
        else {
            this.labelTenderNumber.visible = false;
            this.labelLabel5.visible = false;
            this.buttonLock.visible = false;
            this.classicButton.visible = false;
            this.labelAdvSearch.visible = true;
            this.buttonSuggestedPricing.visible = false;
        }

        if (recordInUse == "Y") {
            this.buttonLock.visible = true;
        } else {
            this.buttonLock.visible = false;
        }
        WindowTitle.refresh("ViewEdiOrder");
    }


    isRecordLocked() {
        if (this.mainDataSource.activeRow?.get("record_locked") == "Y") {
            return true;
        }
        return false;
    }

    unlockTender() {
        const tenderId = this.sourceViewEdiOrder.activeRow?.get("id");

        CommonDialogs.showYesNo("Are you sure you want to unlock this tender [ID: " + tenderId + "]?").then(async response => {
            if (response) {
                this.buttonLock.busy = true;
                if (tenderId != null) {
                    const ediOrderRow = await new ModelEdiOrder().searchSingle({ id: tenderId });
                    const locked = ediOrderRow.get("record_in_use", "");
                    if (locked == "Y") {
                        ediOrderRow.set("record_in_use", "N");
                        ediOrderRow.post();
                        await this.delay(3000).then(() => {
                            log.info("Delay done.  Refreshing.");
                            this.sourceViewEdiOrder.search({ id: tenderId });
                        });
                    }
                }
                this.buttonLock.busy = false;
            }
        });
    }

    async delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    viewOrder() {
        const orderId = this.sourceViewEdiOrder.activeRow?.get("order_id");
        if (orderId != null)
            OrderTabView.navigateTo({ key: orderId, newTab: true })
    }

    layoutOverviewOnDataDisplay(event) {

    }

    private onComponentValidationFailed(result: ValidationResult): void {
        const comp = result.component;
        if (comp?.owner instanceof EdiStop)
            comp.owner.onComponentValidationFailed(result);
    }

    dataheaderEdiOrderOnContextMenuCreation(event: ContextMenuCreationEvent) {
        if (this.activeRow == null || this.mainDataSource.mode !== DataSourceMode.UPDATE) {
            event.preventDefault();
            return;
        }

        const direction = this.mainDataSource.activeRow.get("direction");
        const replyAction = this.mainDataSource.activeRow.get("reply_action");
        const purposeType = this.mainDataSource.activeRow.get("purpose_type");
        const templateType = this.mainDataSource.activeRow.get("edi_template.format_type");
        const orderId = this.mainDataSource.activeRow.get("order_id");
        const tenderId = this.mainDataSource.activeRow.get("id");
        const needsReply = this.replyEligibleToTransmit() && this.needsReply();
        const eligibleNeedsReplyAction = this.replyEligibleToTransmit()
            && this.needsReply()
            && !StringUtil.isEmptyString(replyAction)
            && replyAction === "A";
        const eligibleNoReply = this.replyEligibleToTransmit()
            && !this.needsReply()
            && replyAction === "A";
        const addPurposeActions = direction === "I" && (eligibleNeedsReplyAction || eligibleNoReply || !this.replyEligibleToTransmit());
        const isBatchPresent = this.replyEligibleToTransmit() && !this.needsReply();
        const inboundNeedsReply = direction === "I" && needsReply;
        const ediOrderRow = this.mainDataSource.activeRow;

        event.contextMenu.updateMenuLabels({
            acceptTender: {
                visible: inboundNeedsReply && StringUtil.isEmptyString(replyAction),
                onClick: () => this.acceptTender(ediOrderRow)
            },
            declineTender: {
                visible: inboundNeedsReply && StringUtil.isEmptyString(replyAction),
                onClick: (event) => this.declineTender(event)
            },
            transmitReply: {
                visible: inboundNeedsReply && !StringUtil.isEmptyString(replyAction),
                onClick: () => this.manualTransmitReply()
            },
            retransmitReply: {
                visible: direction === "I" && isBatchPresent,
                onClick: () => this.manualTransmitReply()
            },
            retransmitTender: {
                visible: direction !== "I",
                onClick: () => this.manualTransmitReply()
            },
            rawData: {
                onClick: () => this.openRawDataSlideout(true)
            },
            ediToEnglish: {
                visible: templateType !== "X" && templateType !== "J",
                onClick: () => this.openRawDataSlideout(false)
            },
            makeOrder: {
                visible: addPurposeActions && purposeType === "O" && StringUtil.isEmptyString(orderId),
                onClick: async () => await this.createOrder()
            },
            compareOrder: {
                visible: addPurposeActions && purposeType === "U",
                onClick: async () => await this.compareTender(ediOrderRow, needsReply)
            },
            compareHistory: {
                visible: addPurposeActions && purposeType === "U",
                onClick: async () => this.showCompareHistory(orderId, tenderId)
            },
            cancelOrder: {
                visible: addPurposeActions && purposeType === "C",
                onClick: async () => this.voidOrderAuto(orderId, tenderId, needsReply)
            }
        });
    }

    needsReply(): boolean {
        const replyBatch = this.mainDataSource.activeRow.get("reply_batch_id");
        if (replyBatch == "0" && this.replyEligibleToTransmit()) {
            return true;
        } else {
            return false;
        }
    }

    replyEligibleToTransmit(): boolean {
        const purposeType = this.mainDataSource.activeRow.get("purpose_type");
        const reqReplyOrig = this.mainDataSource.activeRow.get("reply_req_original");
        const reqReplyChange = this.mainDataSource.activeRow.get("reply_req_change");
        const reqReplyCancel = this.mainDataSource.activeRow.get("reply_req_cancel");

        if (purposeType == "O" && reqReplyOrig == "Y" || purposeType == "U" && reqReplyChange == "Y" || purposeType == "C" && reqReplyCancel == "Y") {
            return true;
        } else {
            return false;
        }
    }

    replyRetransmittable(): boolean {
        const replyBatch = this.mainDataSource.activeRow.get("reply_batch_id");

        if (replyBatch > "0" && this.replyEligibleToTransmit()) {
            return true;
        } else {
            return false;
        }
    }

    async findOrderId(ediOrderRow): Promise<any> {
        const partnerId = ediOrderRow.get("partner_id");
        const shipmentId = ediOrderRow.get("shipment_id");
        const version = ediOrderRow.get("version");
        const tenderId = ediOrderRow.get("id");

        return await Api.search("lme/datafusion/find-order-id", { partner_id: partnerId, shipment_id: shipmentId, version: version, tender_id: tenderId });
    }

    async sendReply(ediOrderId: string, orderId?: string): Promise<any> {
        log.info("Replying to tender " + ediOrderId + ".");
        return await Api.post("lme/datafusion/accept-tender", {
            edi_order_id: ediOrderId,
            order_id: orderId
        }).then((response) => {
            const batch = response.data[0].reply_batch;
            const description = response.data[0].description;

            if (batch != "-1") {
                return Promise.resolve(response);
            } else {
                return Promise.reject(response);
            }

        });
    }

    async manualTransmitReply(): Promise<any> {
        if (this.isRecordLocked()) {
            CommonDialogs.showError("Record is currently locked.  Cannot reply.");
            return;
        }
        const ediOrderId = this.mainDataSource?.activeRow?.get("id");

        log.info("Transmitting reply for tender  " + ediOrderId + ".");
        return await Api.post("lme/datafusion/transmit-single-reply", {
            edi_order_id: ediOrderId
        }).then((response) => {
            const batch = response.data[0].reply_batch;
            const description = response.data[0].description;

            this.mainDataSource.search({ "id": ediOrderId });

            if (batch != "-1" && batch != "0") {
                Toast.showSuccessToast("Tender reply transmitted successfully with batch " + batch + ".");
                return Promise.resolve(response);
            }
            else {
                Snackbar.showWarningSnackbar("Unable to transmit reply.  " + description);
                return Promise.reject(response);
            }

        }).catch((error) => {
            log.error("Unable to transmit reply.  " + error);
        });
    }

    async declineTender(event: ClickEvent) {
        if (this.isRecordLocked()) {
            CommonDialogs.showError("Record is currently locked.  Cannot decline.");
            return;
        }
        const ediOrderRow = this.mainDataSource.activeRow;
        const ediOrderProfile = await this.findEdiOrderProfile(ediOrderRow);
        const dt = new DeclineTender();
        dt.ediOrderRow = ediOrderRow;
        dt.declineRequiresCode = ediOrderProfile?.get("decl_req_reason");
        dt.decline();
    }
    refresh(tenderId?: string): void {
        if (tenderId) {
            this.mainDataSource.search({ id: tenderId });
        } else {
            this.mainDataSource.search({ id: this.mainDataSource?.activeRow.get("id") });
        }
    }

    async voidOrderAuto(orderId: string, ediOrderId: string, needsReply: boolean, callback?): Promise<any> {
        return new Promise(async (resolve) => {
            const orderRowStatus = await this.getOrderRowStatus(orderId);
            const ediOrderRow = await new ModelEdiOrder().searchSingle({ id: ediOrderId });
            const replyBatch = ediOrderRow.get("reply_batch_id");

            if (orderRowStatus == "V") {
                if (needsReply && replyBatch == "0") {
                    const prompt = "Order is already voided.  Would you still like to send the reply?";
                    const voidDialog = this.buildCustomYesNoDialog("Void Tender", prompt, "Yes", "No", 490);
                    voidDialog.addUnmountListener(() => {
                        resolve(true);
                    });
                    voidDialog.show().then(async yes => {
                        if (yes) {
                            await this.acceptLoadTender(ediOrderId, orderId);
                        }
                        resolve(true);
                    });
                }
                else {
                    await CommonDialogs.showMessage("Order is already voided.  Removing from Tender Express.").then(() => {
                        this.excludeExistingTender(ediOrderRow, orderId);
                        resolve(true);
                    });
                }
            }
            else {
                Api.post("lme/datafusion/df-void-order", { order_id: orderId, tender_id: ediOrderId }).then(async result => {
                    const status = result.data[0].new_status;
                    const text = result.data[0].text;

                    if (status == "V") {
                        if (needsReply) {
                            await this.acceptLoadTender(ediOrderId, orderId).then(response => {
                                const batch = response.data[0].reply_batch;

                                if (batch != "-1" && batch != "0") {
                                    Toast.showToast(this.getResultsToastPanel(orderId, "Reply sent with batch " + batch + ".", 'successfully voided'), null, { millisUntilDismissal: 7000 });
                                }
                                else {
                                    Snackbar.showWarningSnackbar("Order " + orderId + " successfully voided.  Unable to send reply.");
                                }
                                resolve(true);
                            });
                        }
                        else {
                            Toast.showToast(this.getResultsToastPanel(orderId, "Partner does not require a reply.", 'successfully voided'), null, { millisUntilDismissal: 7000 });
                            resolve(true);
                        }
                    }
                    else {
                        if (needsReply) {
                            CommonDialogs.showYesNo("Order " + orderId + " not voided.  " + text + "\nWould you still like to accept the tender?", "Reply to Tender",
                                {
                                    titleProps: {
                                        imageName: ImageName.circleX,
                                        imageColor: "primary.light"
                                    }
                                }).then(async yes => {
                                    if (yes) {
                                        await this.acceptLoadTender(ediOrderId, orderId).then(response => {
                                            const batch = response.data[0].reply_batch;

                                            if (batch != "-1" && batch != "0") {
                                                Toast.showSuccessToast("Reply sent with batch " + batch + ".");
                                            }
                                            else {
                                                const error = response.data[0].description;
                                                Snackbar.showWarningSnackbar("Unable to send reply.  " + error);
                                            }
                                        });
                                    }
                                    resolve(true);
                                });
                        }
                    }
                });
            }
        });
    }

    async excludeExistingTender(ediOrderRow: RowEdiOrder, orderId: string) {
        ediOrderRow.set("no_display", "Y");
        await ediOrderRow.post();
        return;
    }

    async getOrderRowStatus(orderId: string): Promise<string> {
        const rowOrder = await new ModelOrders().searchSingle({ id: orderId }, "lme/dispatch/Orders");
        const status = rowOrder.get("status", "");
        return status;
    }

    async findEdiOrderRow(ediOrderId: string): Promise<RowEdiOrder> {
        const ediOrderRow = await new ModelEdiOrder().searchSingle({ id: ediOrderId });
        return ediOrderRow;
    }

    async findEdiOrderProfile(ediOrderRow: RowEdiOrder): Promise<RowEdiorderProfile> {
        const profile = await new ModelEdiorderProfile().searchSingle({ partner_id: ediOrderRow.data["partner_id"], version: ediOrderRow.data["version"], direction: ediOrderRow.data["direction"] });
        return profile;
    }

    async acceptTender(ediOrderRow): Promise<any> {
        if (this.mainDataSource?.activeRow == null)
            return;

        const ediOrderId = this.mainDataSource.activeRow.get("id");
        let orderId = this.mainDataSource.activeRow.get("order_id");
        const recordInUse = this.mainDataSource.activeRow.get("record_in_use");

        if (recordInUse == "Y") {
            CommonDialogs.showError("Record is currently locked.  Cannot reply.");
            return;
        }
        const ediOrderProfile = await this.findEdiOrderProfile(ediOrderRow);

        if ("N" == ediOrderRow.get("ignore_map_errors") && 0 < ediOrderRow.get("error_count")) {
                    Snackbar.showWarningSnackbar("Load tender " + ediOrderId + " has errors that must be reviewed prior to order creation.");
                    return;
        }

        this.mainDataSource.setComponentsBusy(true);
        //Original - make order before replying
        //TODO refactor with createOrder
        if (ediOrderRow.get("purpose_type") == "O") {
            let existingOrderId: string = "";
            let existingOrderStatus: string = "";
            let okToMakeOrder = true;
            const duplicateCutoffDays = ediOrderProfile.get("dup_cutoff_days");
            const result = await Api.search("lme/datafusion/orders-lookup-for-tenders", {
                search_shipment_id: ediOrderRow.get("shipment_id"),
                dup_cutoff_days: duplicateCutoffDays
            });
            if (result?.data?.length > 0) {
                existingOrderId = result.data[0].id;
                existingOrderStatus = result.data[0].status;
            }
            else {
                existingOrderId = "";
                existingOrderStatus = "";
            }

            if (!StringUtil.isEmptyString(existingOrderId)) {
                if(existingOrderStatus == "V") {
                    await CommonDialogs.showYesNo("A voided order [" + existingOrderId + "] exists for this shipment id.  Would you like to create a new order?", "Create New Order").then(async clickedYes => {
                                okToMakeOrder = clickedYes;
                            });
                } else {
                    const clickedYes = await CommonDialogs.showYesNo("Order " + existingOrderId + " already exists for this shipment id.  Would you like to swap the purpose of this tender to a Change?",
                                "Order Exists");

                    if (clickedYes) {
                        const purposeSwap = new PurposeSwap();
                        await purposeSwap.swapOriginalToChange(ediOrderRow, existingOrderId).then(() => {
                            this.sourceEdiOrder.search({ id: ediOrderRow.get("id") });
                            this.sourceViewEdiOrder.search({ id: ediOrderRow.get("id") });
                        });
                    }

                    // Exit early - don't proceed to make-order
                    this.mainDataSource.setComponentsBusy(false);
                    return;
                }

            }

            if (okToMakeOrder) {
                await Api.search("lme/datafusion/make-order", {
                "edi_order_id": ediOrderId, "calc_max_target": true
                }).then(async response => {
                    const orderLayout = Layout.getLayout("lme/dispatch/OrderTabView") as OrderTabView;
                    orderLayout["additionalProps"] = { invokedFromEdi: true };
                    const orderPanel = new CrudDecorator({
                        layout: orderLayout,
                        mode: DataSourceMode.ADD,
                        headerProps: {
                            showClose: true,
                            showSaveAndClose: true
                        },
                        doAfterSlideOut: async () => {
                            const orderTabLayout = orderPanel.layout as OrderTabView;
                            const orderId = orderTabLayout?.layoutOrders?.mainDataSource?.activeRow.get("id");
                            const origDestRateId = orderTabLayout.layoutOrders.mainDataSource.activeRow.get("orig_dest_rate_id");
                            const commitmentId = orderTabLayout.layoutOrders.activeRow.get("commitment_id");
                            if (!StringUtil.isEmptyString(orderId)) {
                                const origDestRateId = orderTabLayout?.layoutOrders?.mainDataSource?.activeRow.get("orig_dest_rate_id");
                                const commitmentId = orderTabLayout?.layoutOrders?.mainDataSource?.activeRow.get("commitment_id");
                                MakeOrderUtility.addHandlingRequirements(orderId, orderLayout.layoutOrders);
                                Api.post("lme/datafusion/after-create-order-tasks", {
                                    "edi_order_id": ediOrderId,
                                    "order_id": orderId,
                                    "orig_dest_rate_id": origDestRateId,
                                    "commitment_id": commitmentId
                                }).then(async () => {
                                    const rate: string = orderTabLayout.layoutOrders.mainDataSource.activeRow.get("rate");

                                    if (!StringUtil.isEmptyString(rate)) {
                                        if (parseFloat(rate) != ediOrderRow.get("rate")) {
                                            orderTabLayout.layoutOrders.mainDataSource.activeRow.post(); //repost so rates recalculate
                                        }
                                    }
                                    this.mainDataSource.mode = DataSourceMode.UPDATE;
                                    this.dataHeader.showSave = false;
                                    this.toggleEditFields(false);
                                }).catch(error => {
                                    log.debug(error);
                                    CommonDialogs.showServerError(error);
                                });
                            }
                            //reply
                            await this.replyToTender(orderId, ediOrderId, ediOrderRow);

                        },
                        layoutLoadListeners: [() => {
                            const l = orderPanel.layout as OrderTabView;
                            l.layoutOrders.currencyFromProfile = ediOrderProfile.get("currency_type_id");
                            l.layoutOrders.layoutRates.invokedFromEdi = true;
                            l.layoutOrders.layoutRates.currencyFromProfile = ediOrderProfile.get("currency_type_id");
                            l.layoutOrders.switchRecurringOrder.visible = false;
                            l.layoutOrders.buttonEnterNewOrder.visible = false;
                            l.layoutOrders.panelHowManyOrders.visible = false;
                            l.layoutOrders.dataHeader.showSave = false;
                            l.layoutOrders.tabNextActions.visible = true;
                            l.layoutOrders.loadFromApiData(response.data[0].order, DataSourceMode.ADD);
                            l.layoutOrders.mainDataSource.setProp(true, "isEdi", null);
                            l.layoutOrders.mainDataSource.setProp(ediOrderProfile.get("id"), "ediProfileId", null);
                            l.layoutOrders.imageAddHandlingReq.addClickListener(() => {
                                l.layoutOrders.sourceBrltlOrderHdrXFgp.data = l.layoutOrders.layoutHandlingReqs.sourceBrltlOrderHdrXFgp.data;
                                l.layoutOrders.layoutHandlingReqs.sourceBrltlOrderHdrXFgp.setProp("Y", "madeFromEdi", null);
                            });
                            l.layoutOrders.imageAddHandlingReqOnClick = () => {
                                if (orderLayout.layoutOrders.sourceBrltlOrderHdrXFgp.data.length == 0 && orderLayout.layoutOrders.layoutHandlingReqs.sourceBrltlOrderHdrXFgp.data.length > 0) {
                                    orderLayout.layoutOrders.sourceBrltlOrderHdrXFgp.data = orderLayout.layoutOrders.layoutHandlingReqs.sourceBrltlOrderHdrXFgp.data;
                                    orderLayout.layoutOrders.layoutHandlingReqs.sourceBrltlOrderHdrXFgp.clear();
                                    orderLayout.layoutOrders.layoutHandlingReqs.sourceBrltlOrderHdrXFgp.setProp("Y", "madeFromEdi", null);
                                }
                                OrderHandlingRequirementsSlideout.showSlideout({
                                    hdrDataSource: orderLayout.layoutOrders.sourceBrltlOrderHdrXFgp,
                                    hdrChanged: (row: ModelRow, type: ModelDataChangeType) => orderLayout.layoutOrders.hdrChanged(row, type),
                                    onSave: (hasChanged: boolean) => {
                                        orderLayout.layoutOrders.layoutHandlingReqs.populateHdrs(orderLayout.layoutOrders.sourceBrltlOrderHdrXFgp.data, true, hasChanged).then(() => {
                                            orderLayout.layoutOrders.sourceBrltlOrderHdrXFgp.notifyHasChangedComponents(true);
                                        });
                                    },
                                    invokedFromEdi: true
                                })
                            };
                        }]
                    });
                    orderPanel.slideIn({ speed: 200 });
                    this.mainDataSource?.activeRow.set("record_in_use", "N");

                }).catch(reason => {
                    log.info("Exception making order.  " + reason);
                    Snackbar.showWarningSnackbar("Error encountered creating order : " + reason);
                }).finally(() => {
                    this.mainDataSource.setComponentsBusy(false);
                });
            }
        }
        //Change - compare order before replying
        else if (ediOrderRow.get("purpose_type") == "U") {
            if (StringUtil.isEmptyString(orderId)) {
                const result = await EdiUtility.findOriginalOrderId(ediOrderRow);
                if (result && result.data && result.data.length > 0) {
                    orderId = result.data[0].order_id;
                }
            }

            if (!StringUtil.isEmptyString(orderId)) {
                log.info("Comparing tender [id: " + ediOrderId + "]");
                const compare = new CompareTender();
                this.dataHeader.showSaveAndClose = true;
                this.saveButton.busy = false;
                this.saveButton.enabled = true;

                await compare.compareTender(ediOrderRow, true, this, orderId).then(() => {
                    this.sourceEdiOrder.search({ id: ediOrderRow.get("id") });
                    this.sourceViewEdiOrder.search({ id: ediOrderRow.get("id") });
                    this.mainDataSource.search({ id: ediOrderRow.get("id") });
                });
            }
            else if (StringUtil.isEmptyString(orderId)) {
                const dialog = new LinkTenderDialog({ okVisible: false, title: "Missing Order ID On Change Tender" });
                dialog.ediOrderRow = ediOrderRow;

                dialog.show().then(async response => {
                    if (response) {
                        log.info("Comparing tender [id: " + ediOrderId + "]");
                        const compare = new CompareTender();
                        await compare.compareTender(ediOrderRow, true).then(() => {
                            this.saveButton.busy = false;
                            this.sourceEdiOrder.search({ id: ediOrderRow.get("id") });
                            this.sourceViewEdiOrder.search({ id: ediOrderRow.get("id") });
                        });
                    }
                    else {
                        log.info("User chose not to compare tender [id: " + ediOrderId + "]");
                        this.saveButton.busy = false;
                        this.sourceEdiOrder.search({ id: ediOrderRow.get("id") });
                        this.sourceViewEdiOrder.search({ id: ediOrderRow.get("id") });
                    }
                });
            }
        }
        //Cancel - void order before replying
        else if (ediOrderRow.get("purpose_type") == "C") {
            this.dataHeader.showSaveAndClose = true;
            if (!StringUtil.isEmptyString(orderId)) {
                CommonDialogs.showYesNo("Are you sure you want to void this order [ID: " + orderId + "]?").then(async response => {
                    if (response) {
                        await this.voidOrderAuto(orderId, ediOrderId, true).then(() => {
                            this.sourceViewEdiOrder.search({ id: ediOrderId });
                        });
                    }
                    else {
                        CommonDialogs.showYesNo("Would you still like to accept the tender?").then(async response => {
                            if (response) {
                                log.info("Accepting tender [id: " + ediOrderId + "]");
                                this.acceptLoadTender(ediOrderId, orderId).then(result => {
                                    this.saveButton.busy = false;
                                    this.sourceViewEdiOrder.search({ id: ediOrderId });
                                    this.mainDataSource.search({ id: ediOrderRow.get("id") });
                                });
                            }
                        });
                    }
                });
            }
            else {
                log.info("Order ID not on tender.  Looking for related tenders...");

                await this.findOrderId(ediOrderRow).then(result => {
                    const lookupOrderId = (result.data.length > 0) ? result.data[0].order_id : "";

                    if (!StringUtil.isEmptyString(lookupOrderId)) {
                        this.voidOrderAuto(lookupOrderId, ediOrderId, true);
                    }
                    else {
                        const prompt = "The following tender does not match an existing Order.  Manual action may be necessary " +
                            "to void any related Order.\n\n" +
                            "Bill of Lading: " + ediOrderRow.get("blnum") + "\n" +
                            "Shipment ID: " + ediOrderRow.get("shipment_id") + "\n" +
                            "Tender ID: " + ediOrderRow.get("id") + "\n\n" +
                            "Do you wish to exclude this tender?";
                        const dlg = this.buildCustomYesNoDialog("Reply to Tender", prompt, "Yes", "No", 490) as Dialog;
                        dlg.show().then(async yes => {
                            if (yes) {
                                if (ediOrderRow.get("no_display") != "Y") {
                                    log.info("Excluding edi_order with id [" + ediOrderId + "]");
                                    ediOrderRow.set("no_display", "Y");
                                    ediOrderRow.set("record_in_use", "N");
                                    await ediOrderRow.post().then(async () => {
                                        log.info("Starting delay (3s)...")
                                        await this.delay(3000).then(() => {
                                            log.info("Delay done.  Refreshing.")
                                            this.sourceEdiOrder.search({ id: ediOrderRow.get("id") });
                                            this.sourceViewEdiOrder.search({ id: ediOrderRow.get("id") });
                                        });
                                    });
                                }
                            }
                            else {
                                log.info("Unlocking edi_order with id [" + ediOrderId + "]");
                                ediOrderRow.set("record_in_use", "N");
                                await ediOrderRow.post().then(async () => {
                                    log.info("Starting delay (3s)...")
                                    await this.delay(3000).then(() => {
                                        log.info("Delay done.  Refreshing.")
                                        this.sourceEdiOrder.search({ id: ediOrderRow.get("id") });
                                        this.sourceViewEdiOrder.search({ id: ediOrderRow.get("id") });
                                    });
                                });
                            }

                            await this.acceptLoadTender(ediOrderId, orderId).then(async response => {
                                const batch = response.data[0].reply_batch;
                                Toast.showSuccessToast("Reply sent with batch " + batch + ".");
                                await this.delay(2000).then(() => {
                                    log.info("Delay done.  Refreshing.")
                                    this.sourceEdiOrder.search({ id: ediOrderRow.get("id") });
                                    this.sourceViewEdiOrder.search({ id: ediOrderRow.get("id") });
                                });
                            });

                        });
                    }
                });
            }
        }
    }

    async createOrder(): Promise<any> {
        if (this.isRecordLocked()) {
            CommonDialogs.showError("Record is currently locked.  Cannot make order.");
            return;
        }
        const ediOrderId = this.mainDataSource.activeRow?.get("id");
        const direction = this.mainDataSource.activeRow?.get("direction");
        const ediOrderRow = this.mainDataSource.activeRow;
        const modelEdiOrderRow = await this.findEdiOrderRow(ediOrderId);
        const ediOrderProfile = await this.findEdiOrderProfile(ediOrderRow);
        if ("N" == modelEdiOrderRow.get("ignore_map_errors") && 0 < modelEdiOrderRow.get("error_count")[0]) {
            Snackbar.showWarningSnackbar("Load tender " + ediOrderId + " has errors that must be reviewed prior to order creation.");
            return;
        }
        if (await this.hasInvalidChargeCodes(ediOrderId, direction)) {
            Snackbar.showWarningSnackbar("Load tender " + ediOrderId + " has invalid/missing other charge codes which must be corrected before creating an order.");
            return;
        }
        //TODO refactor with accept tender
        await Api.search("lme/datafusion/make-order", {
            "edi_order_id": ediOrderId, "calc_max_target": true
        }).then(async response => {
            const orderLayout = Layout.getLayout("lme/dispatch/OrderTabView") as OrderTabView;
            orderLayout["additionalProps"] = { invokedFromEdi: true };
            const orderPanel = new CrudDecorator({
                layout: orderLayout,
                providedApiData: response.data[0].order,
                mode: DataSourceMode.ADD,
                headerProps: {
                    showClose: true,
                    showSaveAndClose: true,
                },
                doAfterSlideOut: async () => {
                    const orderTabLayout = orderPanel.layout as OrderTabView;
                    const orderId = orderTabLayout?.layoutOrders?.mainDataSource?.activeRow.get("id");
                    if (!StringUtil.isEmptyString(orderId)) {
                        const origDestRateId = orderTabLayout?.layoutOrders?.mainDataSource?.activeRow.get("orig_dest_rate_id");
                        const commitmentId = orderTabLayout?.layoutOrders?.mainDataSource?.activeRow.get("commitment_id");
                        MakeOrderUtility.addHandlingRequirements(orderId, orderLayout.layoutOrders);
                        Api.post("lme/datafusion/after-create-order-tasks", {
                            "edi_order_id": ediOrderId,
                            "order_id": orderId,
                            "orig_dest_rate_id": origDestRateId,
                            "commitment_id": commitmentId
                        }).then(async () => {
                            this.mainDataSource.mode = DataSourceMode.UPDATE;
                            this.dataHeader.showSave = false;
                            this.toggleEditFields(false);
                            if (!StringUtil.isEmptyString(orderId)) {
                                this.refresh(ediOrderId);
                            }
                        }).catch(error => {
                            log.debug(error);
                            CommonDialogs.showServerError(error);
                        });
                    }
                    await this.replyToTender(orderId, ediOrderId, ediOrderRow);
                },
                layoutLoadListeners: [() => {
                    const l = orderPanel.layout as OrderTabView;
                    l.layoutOrders.currencyFromProfile = ediOrderProfile.get("currency_type_id");
                    l.layoutOrders.layoutRates.invokedFromEdi = true;
                    l.layoutOrders.layoutRates.currencyFromProfile = ediOrderProfile.get("currency_type_id");
                    l.layoutOrders.switchRecurringOrder.visible = false;
                    l.layoutOrders.buttonEnterNewOrder.visible = false;
                    l.layoutOrders.panelHowManyOrders.visible = false;
                    l.layoutOrders.dataHeader.showSave = false;
                    l.layoutOrders.tabNextActions.visible = true;
                    l.layoutOrders.mainDataSource.setProp(true, "isEdi", null);
                    l.layoutOrders.mainDataSource.setProp(ediOrderProfile.get("id"), "ediProfileId", null);
                    l.layoutOrders.loadFromApiData(response.data[0].order, DataSourceMode.ADD);
                    l.layoutOrders.imageAddHandlingReqOnClick = () => {
                        if (orderLayout.layoutOrders.sourceBrltlOrderHdrXFgp.data.length == 0 && orderLayout.layoutOrders.layoutHandlingReqs.sourceBrltlOrderHdrXFgp.data.length > 0) {
                            orderLayout.layoutOrders.sourceBrltlOrderHdrXFgp.data = orderLayout.layoutOrders.layoutHandlingReqs.sourceBrltlOrderHdrXFgp.data;
                            orderLayout.layoutOrders.layoutHandlingReqs.sourceBrltlOrderHdrXFgp.clear();
                            orderLayout.layoutOrders.layoutHandlingReqs.sourceBrltlOrderHdrXFgp.setProp("Y", "madeFromEdi", null);
                        }
                        OrderHandlingRequirementsSlideout.showSlideout({
                            hdrDataSource: orderLayout.layoutOrders.sourceBrltlOrderHdrXFgp,
                            hdrChanged: (row: ModelRow, type: ModelDataChangeType) => orderLayout.layoutOrders.hdrChanged(row, type),
                            onSave: (hasChanged: boolean) => {
                                orderLayout.layoutOrders.layoutHandlingReqs.populateHdrs(orderLayout.layoutOrders.sourceBrltlOrderHdrXFgp.data, true, hasChanged).then(() => {
                                    orderLayout.layoutOrders.sourceBrltlOrderHdrXFgp.notifyHasChangedComponents(true);
                                });
                            },
                            invokedFromEdi: true
                        })
                    };
                }]
            });
            orderPanel.slideIn({ speed: 200 });

        }).catch(reason => {
            log.info("Exception making order.  " + reason);
            Snackbar.showWarningSnackbar("Error encountered creating order : " + reason);
        });
    }
    async acceptLoadTender(ediOrderId: string, orderId?: string): Promise<any> {
        return new Promise(async (resolve) => {
            resolve(await this.sendReply(ediOrderId, orderId));
        });
    }

    private _rearrangeStops() {
        const slideout = new RearrangeStopSlideout("O", this.mainDataSource?.activeRow.get("id"));
        slideout.showSlideout(() => { }, (canceled: boolean) => {
            if (!canceled)
                location.reload();
        });
    }


    textboxCustomerIdOnBlur(event: BlurEvent) {
        if ((this.mainDataSource.mode == DataSourceMode.ADD || this.mainDataSource.mode == DataSourceMode.UPDATE) && !this.mainDataSource.activeRow.isNull("customer_id")) {
            this.stopsTable.rows?.forEach(tableRow => {
                const orderStop = tableRow["orderStop"] as EdiStop;
                orderStop.textboxLocationId.googlePlacesProps.customerId = this.mainDataSource.activeRow?.get("customer_id");
            });
        }
    }

    async checkStopsForDistanceChange() {
        const stopdata = this.sourceEdiStop.data;
        let changedText = this.sourceEdiStop.deletedData?.length > 0 ? "removed from" : null;
        for (let i = 0; changedText == null && i < stopdata?.length; i++) {
            if (stopdata[i]._appending) changedText = "added to";
            if (stopdata[i].getChangedData()?.city_id != null) changedText = "modified on";
        }
        const locked = this.activeRow?.getBoolean("lock_miles", false)
        if (changedText != null) {
            if (locked) {
                const message = `A stop has been ${changedText} this order which may affect mileage.  Would you like to unlock the mileage?`;
                const unlock = await CommonDialogs.showYesNo(message, "Unlock Miles");
                if (unlock) {
                    this.activeRow.set("lock_miles", "N");
                    this.activeRow.set("bill_distance", null);
                }
            } else {
                this.activeRow.set("bill_distance", null);
            }
        }
    }

    public override get crudDecoratorProps(): Partial<LayoutProvidedCrudDecoratorProps> {
        return { customDataLoader: (apiData, mode) => this.loadFromApiData(apiData, mode) };
    }

    public loadFromApiData(apiData: any, mode: DataSourceMode) {
        const ratesLayout = this.layoutRates as EdiOrderRates;
        const alreadyPreventingNotifications = this.mainDataSource.preventChangeNotifications;
        try {
            ratesLayout.calculatingCharges = true;
            this.mainDataSource.preventChangeNotifications = true;
            this.removeChildDataSources();
            // Use appending=false for UPDATE mode (existing data) so hasChanged() works correctly.
            // Only use appending=true for ADD mode (new records).
            const isAppending = mode === DataSourceMode.ADD;
            const order = new ModelRow("lme/datafusion/view-edi-order", isAppending, apiData);
            //set the stop data first so that when we set the order dataSource mode, we can not load the default 2 stops
            this.loadChildTableData(this.stopsTable, this._getArrayAndNull(order, "stops"), mode);
            this.mainDataSource.setRowsAndMode(mode, [order], () => this._displayDataCallback(this.mainDataSource));
            this.loadChildTableData(ratesLayout.tableOtherCharges, this._getArrayAndNull(order, "other_charges"), mode);
            this.showHazmatWarning(this.mainDataSource.activeRow.get("hazmat_code") != null);
            if (apiData?._ctrl_party_txt) {
                this.layoutRates.textboxCtrlParty.textCombined.text = apiData._ctrl_party_txt;
            }

            RateUtil.setAutoRateContext(this.layoutRates, this.mainDataSource.activeRow.get("rate_id"), this.mainDataSource.activeRow.get("orig_dest_rate_id"));
        }
        finally {
            ratesLayout.calculatingCharges = false;
            this.mainDataSource.preventChangeNotifications = alreadyPreventingNotifications;
        }
    }

    private _displayDataCallback(dataSource: DataSource) {
        dataSource.boundComponents.forEach(component => {
            if (component.field != "recurring_order_id")
                dataSource.displayDataInBoundComponent(component);
        });
    }

    private _getArrayAndNull(row: ModelRow, field: string): ModelRow[] {
        const result = row.get(field) || [];
        row.set(field, null);
        return result;
    }

    removeChildDataSources() {
        this.stopsTable.rows?.forEach(tableRow => {
            const orderStop = tableRow["orderStop"] as EdiStop;
            if (orderStop != null) {
                orderStop.commentsAndRefNbrs.sourceEdiStopNote.parentDataSource = null;
                orderStop.commentsAndRefNbrs.sourceEdiRefNumber.parentDataSource = null;
            }
        });
    }

    private loadChildTableData(table: Table, data: any[], mode: DataSourceMode) {
        const tableDataSource = table.dataSource;
        tableDataSource.data = [];
        const modelRows = [];
        // Use appending based on mode - false for UPDATE (existing data), true for ADD (new records)
        const isAppending = mode === DataSourceMode.ADD;
        data.forEach((element: any) => modelRows.push(new ModelRow(tableDataSource.url, isAppending, element)));
        tableDataSource.setRowsAndMode(mode, modelRows);
    }

    clearEquipmentIds(event) {
        if (event.target.text == "") {
            this.textboxEquipmentTypeIdGeneral.text = "";
            this.textboxEquipmentTypeIdEquipment.text = "";
        }
    }

    setDispatchSettings() {
        const dispatchControlSettings = LMEGeneralSettings.getSingleton().getDispatchControl();

        if (dispatchControlSettings.enforce_revenu_cod == 'Y')
            this.textboxRevenueCodeId.required = true;
    }

    textboxBlnumOnBlur(event: BlurEvent) {
        if (!this.mainDataSource.activeRow?.isNull("blnum")) {//&& !this.switchRecurringOrder.checked) {
            let source: DuplicateBOLSource;
            if (this.mainDataSource.mode == DataSourceMode.ADD) {
                source = DuplicateBOLSource.ADD;
            }
            else if (this.mainDataSource.mode == DataSourceMode.UPDATE && this.activeRow.getChangedData()["blnum"] != null) {
                source = DuplicateBOLSource.UPDATE;
            }

            if (source != null) {
                this.checkForDuplicateBOL(source)
                    .then((response) => {
                        if (!response.success)
                            this.textboxBlnum.validationWarning = response.reason;
                    });
            }
        }
    }

    async checkForDuplicateBOL(source: DuplicateBOLSource): Promise<SuccessFail> {
        return await this.checkDuplicateBOL.validate(source, this.saveButton);
    }

    doBeforeExecute(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            // Prevent change notifications during load to avoid false SaveButton activation
            this.mainDataSource.preventChangeNotifications = true;
        }
        if (event.getAction() === DataSourceAction.ADD) {
            this.stopsTable.rows.forEach(
                row => {
                    log.debug(row);
                    row.saveChanges();
                    log.debug(row.data);
                });
            log.debug("stops datasource length " + this.stopsTable.dataSource.data.length);
        }

        if (event.getAction() === DataSourceAction.ADD || event.getAction() === DataSourceAction.UPDATE)
            this.addBeforePostListeners();
    }


    addBeforePostListeners() {
        if (this.mainDataSource.activeRow != null) {
            const row = this.mainDataSource.activeRow;
        }
    }

    async doAfterExecute(event) {
        const tenderId = this.mainDataSource?.activeRow?.get("id", null);

        //TODO: Can we remove some of the profile lookups by setting this here?
        if (this.profile == null) {
            const ediOrderRow = await new ModelEdiOrder().searchSingle({ id: tenderId });
            this.profile = await new ModelEdiorderProfile().searchSingle({ partner_id: ediOrderRow.data["partner_id"], version: ediOrderRow.data["version"] });
        }

        if (event.getAction() === DataSourceAction.ADD || event.getAction() === DataSourceAction.UPDATE) {
            this.activeRow.clearBeforePostListeners();
        }
        if (event.getAction() === DataSourceAction.ADD && this.mainDataSource.activeRow.get("loadboard") === "Y")
            this._displayPostToLoadBoard();
        else if (event.getAction() === DataSourceAction.ADD && this.mainDataSource.activeRow.get("loadboard") === "N")
            this.postOrderNavigateTo();
        else if (event.getAction() === DataSourceAction.SEARCH) {
            const overviewLayout = this.layoutOverview as EdiOrderOverview;
            const orderId: string = this.mainDataSource?.activeRow?.get("order_id", null);
            const moveId: string = this.mainDataSource?.activeRow?.get("shipment_id", null);
            const direction: string = this.mainDataSource.activeRow?.get("direction", null);

            (overviewLayout.layoutStopProgress as EdiStopProgress).stopsTable = this.stopsTable;
            (overviewLayout.layoutStopProgress as EdiStopProgress).setStopsData(orderId, moveId, direction);

            const status = this.mainDataSource.activeRow?.get("movement.status", null);
            const onHold = this.mainDataSource.activeRow?.get("on_hold", null);
            await this.setCommodity();
            this.showHazmatWarning(!this.mainDataSource.activeRow.isNull("hazmat_code"));

            this.textboxTenderStatus.visible = "O" == direction;
            this.textboxEdiError.visible = "O" == direction;
            this.textReadyToSend.visible = "O" == direction;
            this.labelTransmitStatus.visible = "O" == direction;
            this.checkboxNoDisplay.visible = "I" == direction;
            this.textboxRbdTimezoneId.visible = !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("rbd_timezone_id"));

            const replyAction: string = this.mainDataSource.activeRow?.get("reply_action");
            if (replyAction === "D") {
                this.buttonPurposeSwap.enabled = false;
                this.buttonPurposeSwap.color = "strokeSecondary";
            }

            const purposeType = this.mainDataSource?.activeRow?.get("purpose_type", null);
            this.buttonLinkTender.imageColor = "primary";
            this.buttonLinkTender.enabled = (purposeType == "U" || purposeType == "C") ? true : false;
            this.buttonLinkTender.color = (this.buttonLinkTender.enabled) ? "primary" : "strokeSecondary";
            this.buttonLinkTender.visible = "I" == direction;
            this.buttonPurposeSwap.visible = "I" == direction;
            this.tabCarrierPay.visible = "O" == direction;
            let replyButtonVisible = false;
            const funcAckId = this.mainDataSource?.activeRow?.get("func_ack_id");
            if ("I" == direction) {
                this.tabCarrierPay.visible = false;

                if (this.profile != null) {
                    this.tabFA.visible = this.profile.get("send_func_ack") == "Y";
                    replyButtonVisible = this.profile.get("requires_reply") === "Y";
                    this.buttonFuncAckRawData.enabled = funcAckId !== undefined && funcAckId !== null;
                }
            }
            else {
                this.tabFA.visible = true;
                this.buttonFuncAckRawData.enabled = funcAckId !== undefined && funcAckId !== null;
                if (this.mainDataSource?.activeRow?.get("reply_batch_id") > 0) {
                    replyButtonVisible = true;
                }
            }
            this.buttonReplyRawData.visible = replyButtonVisible;


            if (this.mainDataSource.activeRow?.get("missing_xref_label", false) === "Y") {
                this.layoutOverview.imageXRef.visible = true;
                this.layoutOverview.panelErrorIcons.visible = true;
            } else {
                this.layoutOverview.imageXRef.visible = false;
                if (!this.layoutOverview.imageInboundTenderError.visible && !this.layoutOverview.imageOutboundTenderError.visible) {
                    this.layoutOverview.panelErrorIcons.visible = false;
                }
            }
        }

        this.setTitle();
        this.dataheaderEdiOrder.syncTitle();

        this.textboxPreloadTrailerId.visible = this.sourceViewEdiOrder.activeRow?.get("is_asset_company");

        this.verifyHasCommmitment();

        // Re-enable change notifications after search completes.
        // Use a timeout to allow nested child datasources (in dynamically created EdiStop layouts)
        // to complete their async searches before re-enabling.
        // Note: We do NOT call resetOriginalData() here - that would erase user changes!
        // The normal change detection works: ModelRow.set() captures original data on first change.
        if (event.getAction() === DataSourceAction.SEARCH) {
            setTimeout(() => {
                this.mainDataSource.preventChangeNotifications = false;
            }, 1500);
        }
    }

    doAfterModeChange(event: DataSourceModeChangeEvent) {
        this.setCompanySettings(event.newMode);
    }

    enforceActionPermissions(component: any) {
        const isUpdateOrdersAfterBilledDenied = PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_BILLED_ORDER);
        const isBilled = this.mainDataSource.activeRow?.get("is_billed", false);

        const isUpdateOrdersAfterDeliveredDenied = PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_DEL_ORDER);
        const isDelivered = this.mainDataSource.activeRow?.get("status", null) == "D";

        if ((isUpdateOrdersAfterBilledDenied == true && isBilled != null && isBilled == true) ||
            (isUpdateOrdersAfterDeliveredDenied == true && isDelivered != null && isDelivered == true)) {
            const ignoreList = ['layoutImageTable', 'tabsetImages', 'tabImages', 'tableImages', 'buttonMoveDetails', 'tabOverview', 'layoutOverview'];
            if (component != null) {
                component.forEveryChildComponent((child: Component) => child.enabledDuringUpdate = false);
                return;
            }
            this.forEveryChildComponent(comp => {
                if (ignoreList.includes(comp.id))
                    return;
                comp.enabled = false;
                if (comp instanceof Table) {
                    const nestedTable: Table = comp as Table
                    nestedTable.addRowDisplayListener((event: TableRowDisplayEvent) => {
                        const row: TableRow = (event as TableRowDisplayEvent).getTableRow();
                        row.forEveryChildComponent(child => {
                            child.enabledDuringUpdate = false;
                        });
                    });
                }
            });
        }
    }

    doBeforeModeChange(event) {
        this.tabOverview.visible = event.newMode === DataSourceMode.UPDATE || event.newMode === DataSourceMode.NONE;
        this.tabStops.visible = event.newMode !== DataSourceMode.SEARCH;
        this.setTitle();
        this.dataheaderEdiOrder.syncTitle();

    }

    public async addNewStopToTable(): Promise<TableRow> {
        let addRowResult: TableAddRowResult;
        const newRowData = await this.stopsTable._createNewRowData();
        if (newRowData != null) {
            newRowData.set("stop_type", this.stopsTable.data.length > 0 ? "SO" : "PU");
            this.stopsTable.data.push(newRowData);
            addRowResult = this.stopsTable.addRow(newRowData, { mode: TableRowMode.ADD }, { display: true, addToData: true });
        }
        return addRowResult.row;
    }

    private async setCommodity() {

        const commodityId = this.mainDataSource.activeRow.get("commodity_id");
        if (commodityId === null || commodityId === undefined) {
            return;
        }
        const response = await Model.searchSingleRecord("lme/dispatch/commodity", { id: commodityId });

        if (response != null) {
            const lmModelRow = new ModelRow(this.textboxCommodityId.lookupModel, false, {
                id: response.get("id"),
                descr: response.get("descr"),
                temperature_min: response.get("temperature_min"),
                temperature_max: response.get("temperature_max"),
                is_hazmat: response.get("is_hazmat"),
                hazmat_code: response.get("hazmat_number"),
                hazmat_number: response.get("hazmat_number"),
                hazmat: response.get("hazmat")
            });

            lmModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
            this.textboxCommodityId.dataSource.activeRow.setLookupModelData("commodity_id", lmModelRow);
            this.textboxCommodityId.dataSource.activeRow.set("commodity_id", response.get("id"));
            this.textboxCommodityId.displayData(this.textboxCommodityId.dataSource.activeRow, null, 0);

            this.textboxHazmatCode.dataSource.activeRow.setLookupModelData("hazmat_code", lmModelRow);

            this.textboxHazmatCode.displayData(this.textboxHazmatCode.dataSource.activeRow, null, 0);

            this.showHazmatWarning(this.textboxCommodityId.getFirstLookupModelData()?.get("hazmat_number") != null);
        }

    }

    commodityCodeChanged() {
        const lookupData = this.textboxCommodityId.getFirstLookupModelData();
        if (lookupData == null)
            return;
        this.textboxCommodity.text = lookupData?.get("descr");

        this.showHazmatWarning(lookupData?.get("hazmat_number") != null);
        const lmModelRow = new ModelRow(this.textboxHazmatCode.lookupModel, false, { id: lookupData?.get("hazmat_number") });
        this.mainDataSource.activeRow.setLookupModelData("hazmat_code", lmModelRow);
        this.mainDataSource.activeRow.set("hazmat_code", lookupData?.get("hazmat_number"));

        this.mainDataSource.activeRow.set("hazmat", lookupData?.get("hazmat", "N"));
        this.mainDataSource.activeRow.set("temperature_min", lookupData?.get("temperature_min"));
        this.mainDataSource.activeRow.set("temperature_max", lookupData?.get("temperature_max"));
    }

    showHazmatWarning(hazmat: boolean) {
        this.panelCommodityHazmat.borderWidth = hazmat ? 1 : 0;
        this.panelCommodityHazmat.borderColor = hazmat ? "error" : "";
        this.labelHazmatCommodity.visible = hazmat;
    }

    textboxAppliesToOnChange(event: ChangeEvent) {
        if (!event.userInitiatedChange)
            return;
        const appliesTo: Textbox = event.target as Textbox;
        const equipType = TableRow.getContainingTableRow(appliesTo).findComponentById("textboxMatchEquipmentTypeId") as Textbox;
        equipType.text = null;
        equipType.updateBoundData(equipType.boundRow, this.mainDataSource.mode);
    }

    textboxMatchEquipmentTypeIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.applies_to = "in Y,X,D";
    }

    buttonAddAnotherStopOnClick(event: ClickEvent) {
        this.addNewStopToTable().then(row => row.scrollIntoView());
    }

    buttonAddAnotherStopOnDataDisplay(event: DataDisplayEvent) {
        (event.target as Button).setProps({ ...(event.target as Button).allProps, "color": "primary" });
    }

    //** This is an event handler for stopsTable onRowBeforeSave */
    tableStopBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const tableRow: TableRow = event.target as TableRow;
        const modelRow: ModelRow = tableRow.data;
        const orderStopLayout: EdiStop = event.target["orderStop"] as EdiStop;
        modelRow.set("stop_notes", orderStopLayout.commentsAndRefNbrs.tableStopComments?.data || []);
        modelRow.set("stop_reference_numbers", orderStopLayout.commentsAndRefNbrs.tableStopReferenceNumbers?.data || []);
        const changedData = modelRow.getChangedData();
        if (AppointmentUtil.apptConfirmCheck(changedData, modelRow))
            orderStopLayout.getAppointmentChangedRow();
        else {
            modelRow.set("appointment_change", null);
        }
    }

    textboxRecurringOrderIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
    }

    /** This is an event handler for the onRowDisplay event of stopsTable.  */
    stopsTableOnRowDisplay(event) {
        const tableRow: TableRow = event.getTableRow();
        const labelSequence = (tableRow.findComponentById("labelSequence")) as Label;
        labelSequence.text = (tableRow.index + 1).toString();
        tableRow.findComponentById((comp: any) => {
            if (comp instanceof EdiStop) {
                comp.addLayoutLoadListener(() => {
                    this.enforceActionPermissions(tableRow);
                    this.sourceEdiStop.preventChangeNotifications = true;
                    tableRow["orderStop"] = comp;
                    labelSequence.addClickListener(event => tableRow.showEditLayout());
                    comp.calculateRates = () => this.checkStopsForDistanceChange().then(async () => await this.layoutRates.calculateRates(false, false));
                    comp.loadingRecurringOrder = this.loadingRecurringOrder
                    comp.initialize(false, tableRow.index, this.sourceViewEdiOrder, this.sourceEdiStop);
                    if (this.getInvokedFromEdi() || this.loadingRecurringOrder) {
                        comp.setFreeFormVisibility();
                        if (this.loadingRecurringOrder) {
                            comp.doAfterLocationIdChanged()
                            if (tableRow.index > 0)
                                this.loadingRecurringOrder = false;
                        }
                    }
                    comp.displayData(tableRow.data, null, null);
                    if (tableRow.index == 0) {
                        comp.switchPalletsRequired.displayData(this.sourceViewEdiOrder.activeRow, null, 0);
                        comp.textboxPalletsHowMany.displayData(this.sourceViewEdiOrder.activeRow, null, 0);
                        comp.marginRight = 36;
                    }
                    const commentsTable = comp.commentsAndRefNbrs.tableStopComments;
                    const tableRefNbrs = comp.commentsAndRefNbrs.tableStopReferenceNumbers;
                    this.sourceEdiStop.preventChangeNotifications = false;
                    const txtLocId = comp.textboxLocationId;
                    const txtCases = comp.textboxCases;
                    const ediCityState = comp.ediCityState;

                    txtLocId.captionVisible = true;
                    ediCityState.captionVisible = true;

                    const originalOrder = this.mainDataSource.activeRow?.get("original_order");
                    const changedOrder = this.mainDataSource.activeRow?.get("changed_order");
                    const enableLocation = StringUtil.isEmptyString(this.mainDataSource.activeRow?.get("order_id")) && ("Y" === originalOrder || "Y" === changedOrder);
                    txtLocId.enabled = enableLocation;
                    txtCases.enabled = enableLocation;
                    commentsTable.addRowDisplayListener((event: TableRowDisplayEvent) => this.enforceActionPermissions((event as TableRowDisplayEvent).getTableRow()));
                    tableRefNbrs.addRowDisplayListener((event: TableRowDisplayEvent) => this.enforceActionPermissions((event as TableRowDisplayEvent).getTableRow()));
                });
                return true;
            }
            return false;
        });
    }

    private revenueCodeChanged(newValue: any) {
        Api.search("lme/dispatch/calculate-order-mode",
            {
                "mode_locked": false,
                "revenue_code_id": newValue,
                "order_type_id": this.mainDataSource.activeRow.get("order_type_id"),
                "current_mode": this.mainDataSource.activeRow.get("order_mode")
            }).then(response => {
                const data = response.data[0];
                const newMode = data["mode"];
                this.mainDataSource.activeRow.set("order_mode", newMode);
            });
    }

    private orderTypeChanged(newValue: any) {
        Api.search("lme/dispatch/calculate-order-mode",
            {
                "mode_locked": false,
                "revenue_code_id": this.mainDataSource.activeRow.get("revenue_code_id"),
                "order_type_id": newValue,
                "current_mode": this.mainDataSource.activeRow.get("order_mode")
            }).then(response => {
                const data = response.data[0];
                const newMode = data["mode"];
                this.mainDataSource.activeRow.set("order_mode", newMode);
            });
    }

    displayOrderUsersSlideout() {
        const urs = new UsersResponsibilitySlideoutTrans("lme/datafusion/EdiOrderUsers", this.getEdiOrderUsersSlideoutTitle(), this.getEdiOrderUsersIdFilter(),
            this.getEdiOrderUsersSharedFieldNames(), this.sourceViewEdiOrder, this.sourceResponsibleHist);
        urs.show();
    }

    private getEdiOrderUsersSlideoutTitle(): string {
        const name = this.sourceViewEdiOrder.activeRow?.get("id");
        return "Tender Users" + ((StringUtil.isEmptyString(name) === false) ? (" - " + name) : "");
    }

    private getEdiOrderUsersIdFilter(): any {
        const id = this.sourceViewEdiOrder.activeRow?.get("id");
        if (id != null)
            return { id: id };
        return null;
    }

    private getEdiOrderUsersSharedFieldNames(): string[] {
        return ["operations_user", "agent_payee_id", "entered_user_id"];
    }

    /** This is an event handler for the beforeExecution event of sourceLocation.  It's gross. */
    sourceOrdersAfterGetDataboundValues(row: ModelRow, dataSource: DataSource) {
        if (dataSource.mode === DataSourceMode.SEARCH) {
            //have to set these fields when in search mode because they aren't bound fields for the main layout
            for (const field of this.getEdiOrderUsersSharedFieldNames()) {
                const fieldValue = dataSource.searchRow.get(field);
                row.set(field, fieldValue);
            }
        }
        if (ArrayUtil.isEmptyArray(this.sourceResponsibleHist?.data)) {
            return;
        }
        row.addLinkedModel({ model: this.sourceResponsibleHist.url, rows: [this.sourceResponsibleHist.data[0]] });
    }

    private _displayPostToLoadBoard() {
        const id = this.mainDataSource.activeRow?.data?.["id"];
        if (id) {
            const layout = Layout.getLayout("lme/dispatch/PostToLoadBoards", {
                maxWidth: 900,
                width: window.innerWidth * .60,
                backgroundColor: "defaultBackground",
                fillHeight: true, paddingBottom: 50
            }) as PostToLoadBoards;

            const erd = new EditRowDecorator({
                title: "Post to Load Boards - Order " + id,
                layout: layout,
                layoutLoadListeners: event => { },
                fillVerticalSpace: true,
                width: 900,
                overlayProps: { greyedBackground: true },
                onSave: () => {
                    erd.multiButton.busy = true;

                    layout.postDataSources()
                        .then(() => layout.slideOut())
                        .finally(() => erd.multiButton.busy = false);
                },
                onClose: (cancelled: boolean) => layout.slideOut(),
                doAfterSlideOut: (decorator: SlideoutDecorator) => this.postOrderNavigateTo(),
                doAfterSlideIn: () => {
                    layout.mainDataSource.data[0] = this.mainDataSource.data[0];
                    layout.mainDataSource.rowIndex = 0;
                    layout.mainDataSource.mode = DataSourceMode.UPDATE;
                }
            });
        }
    }

    textboxPnnLoadboardUserIdBeforeLookupModelSearch(event) {
        event.filter.web_user_type = "U";
    }

    public static navigateTo(id: string, newTab = true): void {
        OrderTabView.navigateTo({ key: id, newTab: newTab });
    }

    async postOrderNavigateTo(): Promise<any> {
        let key = null;
        switch (this.nextAction) {
            case NextAction.VIEW_ORDER:  // view order list
                const duplicateOrders: Array<String> = this.mainDataSource?.activeRow?.get("duplicate_orders", []);
                const orderIds = [this.mainDataSource?.activeRow?.get("id"), ...duplicateOrders];
                if (orderIds.length > 1) {
                    return Navigation.navigateTo("lme/dispatch/OrderList?ids=" + orderIds.toString(), { newTab: false });
                }
                else {
                    key = this.mainDataSource?.activeRow?.get("id");
                    return OrderTabView.navigateTo({ key, newTab: false });
                }
            case NextAction.NEW_ORDER:  // new order
                key = this.mainDataSource?.activeRow?.get("id");
                return OrderTabView.navigateTo({ newTab: false, additionalProps: { successAdd: true, orderId: key } });
            case NextAction.ASSIGN_CARRIER:  // assign carrier
                key = this.mainDataSource?.activeRow?.get("curr_movement_id");
                return OrderTabView.navigateTo({
                    key,
                    orderId: this.activeRow.get("id"),
                    newTab: false,
                    additionalProps: { assignCarrier: true }
                });
            case NextAction.ASSIGN_DRIVER:  // assign driver
                const orderId = this.mainDataSource?.activeRow?.get("id");
                key = this.mainDataSource?.activeRow?.get("curr_movement_id");
                return OrderTabView.navigateTo({ key, orderId: orderId, newTab: false, additionalProps: { successAdd: true, openPreassignment: true } });
            case NextAction.FIND_CARRIER:  // find carrier
                if (this.activeRow.getBoolean("is_brokerage_ltl")) {
                    key = this.mainDataSource?.activeRow?.get("id");
                    return OrderTabView.navigateTo({ key, newTab: false, additionalProps: { selectCarrier: true } });
                } else {
                    key = this.mainDataSource?.activeRow?.get("curr_movement_id");
                    return OrderTabView.navigateTo({
                        key,
                        orderId: this.activeRow.get("id"),
                        newTab: false,
                        additionalProps: { findCarrier: true }
                    });
                }
            default:
                return Promise.resolve();
        }
    }

    set nextAction(nextAction: NextAction) {
        this._nextAction = nextAction;
    }

    get nextAction(): string | (() => void) {
        return this._nextAction;
    }

    private postOrderActionButtonClick(event: ClickEvent) {
        const button = event.target as Button;
        button.borderWidth = 1;
        button.color = "primary.reverse";
        button.backgroundColor = "primary";
        button.borderColor = "primary"
    }

    /** This is an event handler for the onClick event of buttonAssignCarrier.  */
    buttonAssignCarrierOnClick(event: ClickEvent) {
        this.nextAction = NextAction.ASSIGN_CARRIER;
        this.postOrderActionButtonClick(event);
    }

    /** This is an event handler for the onClick event of buttonFindCarrier.  */
    buttonFindCarrierOnClick(event: ClickEvent) {
        this.nextAction = NextAction.FIND_CARRIER;
        this.postOrderActionButtonClick(event);
    }

    /** This is an event handler for the onClick event of buttonEnterNewOrder.  */
    buttonEnterNewOrderOnClick(event: ClickEvent) {
        this.nextAction = NextAction.NEW_ORDER;
        this.postOrderActionButtonClick(event);
    }

    /** This is an event handler for the onClick event of buttonViewOrder.  */
    buttonViewOrderOnClick(event: ClickEvent) {
        this.nextAction = NextAction.VIEW_ORDER;
        this.postOrderActionButtonClick(event);
    }

    stopTableOnEditLoaded(): (rowDecorator: EditRowDecorator, tableRow: TableRow) => void {
        return (rowDecorator: EditRowDecorator, tableRow: TableRow) => {
            const thisOrderStop = tableRow["orderStop"] as EdiStop;
            const editOrderStop = rowDecorator.layout as EdiStop;
            if (this.sourceViewEdiOrder.isAddingOrUpdating()) {
                editOrderStop.commentsAndRefNbrs.tableStopComments.dataSource.mode = DataSourceMode.UPDATE;
                editOrderStop.commentsAndRefNbrs.tableStopReferenceNumbers.dataSource.mode = DataSourceMode.UPDATE;
            }
            editOrderStop.mainDataSource.activeRow?.set("stop_notes", thisOrderStop?.commentsAndRefNbrs.tableStopComments.data);
            editOrderStop.mainDataSource.activeRow?.set("reference_numbers", thisOrderStop?.commentsAndRefNbrs.tableStopReferenceNumbers.data);
            editOrderStop.initialize(true, tableRow.index, this.sourceViewEdiOrder, this.sourceEdiStop);
            editOrderStop.slideOutTableRow = tableRow;
            const originalOrder = this.mainDataSource.activeRow?.get("original_order");
            const changedOrder = this.mainDataSource.activeRow?.get("changed_order");
            editOrderStop.textboxLocationId.enabled = StringUtil.isEmptyString(this.mainDataSource.activeRow?.get("order_id")) && ("Y" === originalOrder || "Y" === changedOrder);
            editOrderStop.layoutStopShowAsButton.visible = false;
            rowDecorator.onSave = this.stopSlideoutOnSave(rowDecorator, tableRow);
            rowDecorator.btnDelete.visible = false;
            rowDecorator.layout.width = window.innerWidth * .75;
            rowDecorator.labelTitle.text = "Tender Stop Details - " + this.title;
            rowDecorator.multiButton.dropdownItems = null;
        };
    }

    stopSlideoutOnSave(rowDecorator: EditRowDecorator, tableRow: TableRow): (updatedData: ModelRow | any) => void {
        const orderStop = (rowDecorator.layout as EdiStop);
        const thisOrderStop = tableRow["orderStop"] as EdiStop;
        return async (updatedData: ModelRow | any) => {
            tableRow.saveChangesFromEdit(updatedData);
            if (tableRow.index == 0) {
                this.sourceViewEdiOrder.activeRow.set("pallets_how_many", orderStop.textboxPalletsHowMany.text);
                this.sourceViewEdiOrder.activeRow.set("pallets_required", true === orderStop.switchPalletsRequired.checked ? "Y" : "N");
            }
            orderStop.commentsAndRefNbrs.saveChangesFromEdit(tableRow["orderStop"].commentsAndRefNbrs);
            thisOrderStop.textboxLocationId.displayData(tableRow.data, null, 0);
            thisOrderStop.setLocationTextAfterLocation(thisOrderStop.textboxLocationId.textCombined, tableRow.data, "location_id");
            await thisOrderStop.activeRow?.post();
            thisOrderStop.activeRow.set("initial_appointment_change", null); // reset in scenario if user exits slideout and makes another update on main screen
        };
    }

    viewLoadTenders() {
        //TODO Cleanup
        const orderId = this.mainDataSource.activeRow?.data?.["order_id"];
        log.debug("id", orderId);
        if (orderId) {
            const layout = Layout.getLayout("lme/datafusion/OrderLoadTenderHistory", {
                maxWidth: 900,
                width: window.innerWidth * .60,
                backgroundColor: "defaultBackground",
                fillHeight: true,
                paddingBottom: 50
            }) as OrderLoadTenderHistory;

            new SlideoutDecorator({
                layout: layout,
                title: "Load Tenders - Order " + orderId,
                fillVerticalSpace: true,
                doAfterSlideIn: (decorator: SlideoutDecorator) => layout.mainDataSource.search({ order_id: orderId })
            });

            layout.orderId = orderId;
        }
    }

    /** This is an event handler for the onChange event of switchPostLoadboard.  */
    switchPostLoadboardOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const switchPostLoadboard = event.target as Switch;
            const onHold = this.mainDataSource.activeRow.get("on_hold");
            if (switchPostLoadboard != null && switchPostLoadboard.checked && onHold == "Y")
                this.createPostToLoadBoardDialog().then(() => switchPostLoadboard.checked = false);
        }
    }

    createPostToLoadBoardDialog() {
        const dialog = new Dialog({ okVisible: true });
        const lblCaptionLine1 = new Label({
            text: "Orders on hold may not be posted to the load boards.",
            align: HorizontalAlignment.CENTER,
            fontSize: "large"
        });
        const lblCaptionLine2 = new Label({
            text: "Remove the hold to post this order.",
            align: HorizontalAlignment.CENTER,
            fontSize: "large"
        })
        const pnlCaption = new Panel({ marginRight: 8, verticalAlign: VerticalAlignment.TOP, fillHeight: true, rowBreak: false })
        pnlCaption.add(lblCaptionLine1, lblCaptionLine2);

        const pnlContainer = new Panel({ fillRow: true, maxWidth: 550 });
        pnlContainer.add(pnlCaption);
        dialog.add(pnlContainer);
        return dialog.show();

    }

    createUpdateDeniedDialog() {
        const dialog = new Dialog({ okVisible: true });
        const lblCaptionLine1 = new Label({
            text: "Order has been billed and you do not have permission to update an order " +
                "after the order has been billed",
            align: HorizontalAlignment.CENTER,
            fontSize: "large"
        });
        const pnlCaption = new Panel({ marginRight: 8, verticalAlign: VerticalAlignment.TOP, fillHeight: true, rowBreak: false });
        pnlCaption.add(lblCaptionLine1);
        const pnlContainer = new Panel({ fillRow: true, maxWidth: 550 });
        pnlContainer.add(pnlCaption);
        dialog.add(pnlContainer);
        return dialog.show();
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.edi.EntryEdiOrder", this.mainDataSource.activeRow.get("id"));
    }

    public get doAfterAction(): () => void {
        return this._doAfterAction;
    }

    public set doAfterAction(value: () => void) {
        this._doAfterAction = value;
    }

    private executeDoAfterAction() {
        if (this.doAfterAction != null)
            this.doAfterAction();
    }

    createRecurringOrder(event: ClickEvent) {
        const orderId = this.mainDataSource.activeRow?.get("id");
        if (orderId == null) return;

        function makeApiCall(userEnteredRecurringOrderId?: String) {
            Api.post("lme/dispatch/create-recurring-order", { order_id: orderId, recurring_order_id: userEnteredRecurringOrderId }).then(output => {
                const recurringOrderId = output.data[0].recurring_order_id;
                const toastMessage = `Recurring Order ${recurringOrderId} has been created.`;
                Toast.showSuccessToast(toastMessage);
            }).catch(error => {
                for (const message of error.messages) {
                    if (message.includes("already exists in the table")) {
                        Snackbar.showWarningSnackbar("Duplicate recurring order ID");
                        return;
                    }
                }
                Snackbar.showWarningSnackbar(error.messages[0]);
            });
        }

        if (getDispatchControlBoolean("recurring_override", false)) {
            const dialogLayout = Layout.getLayout("lme/dispatch/RecurringOrderPrompt") as RecurringOrderPrompt;
            CommonDialogs.showYesNo(dialogLayout, "Create Recurring Order", { yesButtonCaption: "OK", noButtonCaption: "Cancel" }, false, 150).then(clickedYes => {
                if (clickedYes) {
                    const userEnteredRecurringOrderId: String = dialogLayout.textboxRecurringOrderId.text;
                    makeApiCall(userEnteredRecurringOrderId);
                }
            });
        }
        else {
            makeApiCall();
        }
    }

    voidOrder(event: ClickEvent) {
        const orderId = this.mainDataSource.activeRow?.get("id");
        if (orderId != null) {
            VoidOrderPrompt.showPrompt(orderId, (success: boolean, promptContent: VoidOrderPrompt) => {
                if (success) {
                    const toastMessage = `Order ${orderId} has been voided.`;
                    Toast.showSuccessToast(toastMessage);
                    new Promise(resolve => setTimeout(resolve, 2500)).then(() => this.executeDoAfterAction());
                } else {
                    const snackMessage = `Could not void order ${orderId}`;
                    Snackbar.showWarningSnackbar(snackMessage);
                }
            });
        }
    }

    imageReorderOnClick(event: ClickEvent) {
        this._rearrangeStops();
    }

    equipItemTableOnRowDisplay(event) {
        const row: TableRow = event.getTableRow();
        const equipType = row.findComponentById("textboxMatchEquipmentTypeId") as Textbox;
        equipType.addLookupModelSelectionListener(this.equipTypeOnLookupModelSelection);
    }

    private equipTypeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true)
            event.target.boundRow.set("applies_to", event.selection.get("applies_to"));
    }

    public set invokedFromEdi(value: boolean) {
        this._invokedFromEdi = value;
    }
    public getInvokedFromEdi(): boolean {
        return this._invokedFromEdi;
    }

    stopsTableOnContentsChanged(event: TableContentsChangedEvent) {
        if (TableAction.DELETE == event.getAction()) {
            this.checkStopsForDistanceChange().then(() => {
                if (this.layoutRates.checkPickupAndDeliveryEntered()) this.layoutRates.calculateRates(false, false);
            });
        }
        const data = event.getDataSource().data;
        log.info("Found data");
    }

    private get saveButton(): SaveButton {
        return this.dataHeader?.["saveButton"];
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.color = "subtle.light";
            tab.headingLabel.setProps({ color: "default", fontBold: true, fillRow: false })
            if (tab["countLabel"] == null) {
                tab["countLabel"] = new Label({
                    fontBold: true, text: "0", fillRow: true,
                    color: "primary", rowBreak: false, marginLeft: 5,
                    id: "labelErrorCount"
                });
                tab.heading.insert(tab["countLabel"], 1);
            }
        }
    }

    sourceEdiTenderErrorsAfterExecution() {
        const replyErrorsCount = this.sourceEdiMapError.data.filter(error => error.data["transaction_type"] === "R").length;
        const tenderErrorArray = this.sourceEdiMapError.data.filter(error => error.data["transaction_type"] === "T");

        const tenderWarningAndErrorCount = tenderErrorArray.length;
        const tenderWarningCount = tenderErrorArray.filter(error => error.data["error_type"] === "W").length;
        const tenderErrorsCount = tenderErrorArray.filter(error => error.data["error_type"] === "E").length;
        const ignoreMapErrors = this.mainDataSource?.activeRow?.get("ignore_map_errors");
        const direction = this.mainDataSource.activeRow?.get("direction", null);

        if (this.tabErrors != null && this.tabErrors["countLabel"] != null) {
            this.tabErrors["countLabel"].text = tenderWarningAndErrorCount;
            if (tenderWarningAndErrorCount > 0) {
                if (tenderWarningCount > 0 && tenderWarningCount == tenderWarningAndErrorCount) {
                    this.tabErrors["countLabel"].color = "caution";
                } else {
                    this.tabErrors["countLabel"].color = "error";
                }
                if (ignoreMapErrors == "N") {
                    this.layoutOverview.panelErrorIcons.visible = true;
                    if (tenderErrorsCount > 0) {
                        this.layoutOverview.imageInboundTenderError.visible = direction == "I";
                    } else {
                        this.layoutOverview.imageInboundWarning.visible = direction == "I";
                    }
                    this.layoutOverview.imageOutboundTenderError.visible = direction == "O";
                } else {
                    this.layoutOverview.imageInboundTenderError.visible = false;
                    this.layoutOverview.imageOutboundTenderError.visible = false;
                    this.layoutOverview.imageInboundWarning.visible = false;
                    if (!this.layoutOverview.imageXRef.visible) {
                        this.layoutOverview.panelErrorIcons.visible = false;
                    }
                }
            }
        }
        this.layoutOverview.imageReplyError.visible = replyErrorsCount > 0;

    }

    private shouldShowTransmissionStatus(status: string, showTransmissionLog: boolean): boolean {
        const invalidStatuses = ["(Not Sent)", "(Unknown)", undefined];
        return showTransmissionLog || !invalidStatuses.includes(status);
    }

    private buildTransmissionStatusSearchInput(): Record<string, any> {
        const activeRow = this.sourceViewEdiOrder.activeRow;
        const direction = activeRow.get("direction");
        const partnerId = activeRow.get("partner_id");
        const altPartnerId = activeRow.get("alt_partner_id");
        const version = activeRow.get("version");
        const batch = activeRow.get("gs06_group_cntlno");
        const isaCntNo = activeRow.get("isa13_intr_cntlno");

        const baseInput = {
            direction: direction,
            func_batch: batch,
            batch: isaCntNo,
            transaction_type: "T",
            func_version: version
        };

        if (direction === "I") {
            return {
                ...baseInput,
                func_sender_id: partnerId,
                sender_id: altPartnerId
            };
        } else {
            return {
                ...baseInput,
                func_receiver_id: partnerId,
                receiver_id: altPartnerId
            };
        }
    }

    setTransmissionStatusTooltipContent(component: Component, showTransmissionLog: boolean = false) {
        component.tooltipDynamicContent = async () => {
            const status = this.sourceViewEdiOrder.activeRow?.get("transmit_status");

            if (this.shouldShowTransmissionStatus(status, showTransmissionLog)) {
                const layout = await Layout.getLoadedLayout("lme/datafusion/TransmissionStatusQuickInfo") as TransmissionStatusQuickInfo;
                const searchInput = this.buildTransmissionStatusSearchInput();

                await layout.sourceEdiLog?.search(searchInput);
                return layout;
            }

            return null;
        };
    }

    getResultsToastPanel(orderId: string, message: string, action: string): Panel {
        const labelProps = { color: "white", fontSize: "xlarge", rowBreak: false, paddingLeft: 3, paddingRight: 1, marginLeft: 0, marginRight: 0, wrap: false };
        const labelLinkProps = { ...labelProps, style: { textDecoration: "underline" } };

        if (!StringUtil.isEmptyString(orderId)) {
            return new Panel({
                align: HorizontalAlignment.CENTER, fillRow: true,
                components: [
                    new Label({ ...labelProps, text: "Order " }),
                    new Label({ ...labelLinkProps, text: orderId, onClick: event => OrderTabView.navigateTo({ key: orderId, newTab: true }) }),
                    new Label({ ...labelProps, text: " " + action + ".  " + message })
                ]
            });
        }
        else {
            return new Panel({
                align: HorizontalAlignment.CENTER, fillRow: true,
                components: [
                    new Label({ ...labelProps, text: "Order not " + action + ".  " + message })
                ]
            });
        }
    }

    public get profile(): RowEdiorderProfile {
        return this._profile;
    }
    public set profile(value: RowEdiorderProfile) {
        this._profile = value;
    }

    public get ediOrderRow(): RowEdiOrder {
        return this._ediOrderRow;
    }
    public set ediOrderRow(value: RowEdiOrder) {
        this._ediOrderRow = value;
    }

    async linkTender(event) {
        if (this.isRecordLocked()) {
            CommonDialogs.showError("Record is currently locked.  Cannot link tender.");
            return;
        }
        const tenderId = this.mainDataSource?.activeRow?.get("id", null);
        const purposeType = this.mainDataSource?.activeRow?.get("purpose_type", null);

        if (tenderId != null) {
            const ediOrderRow = await new ModelEdiOrder().searchSingle({ id: tenderId });
            const profile = await new ModelEdiorderProfile().searchSingle({ partner_id: ediOrderRow.data["partner_id"], version: ediOrderRow.data["version"] });
            let needsReply: boolean = false;

            const dialog = new LinkTenderDialog({ okVisible: false, title: "Missing Order ID On Change Tender" }) as LinkTenderDialog;
            dialog.ediOrderRow = ediOrderRow;
            if (purposeType == "U") {
                needsReply = profile.get("rply_rquired_chang") == "Y" ? true : false;
            }
            else if (purposeType == "C") {
                needsReply = profile.get("rply_rquired_cance") == "Y" ? true : false;
            }

            dialog.show().then(async response => {
                if (response) {
                    log.info("Comparing tender [id: " + tenderId + "]");
                    const compare = new CompareTender();
                    await compare.compareTender(ediOrderRow, needsReply).then(() => {
                        this.sourceEdiOrder.search({ id: ediOrderRow.get("id") });
                        this.sourceViewEdiOrder.search({ id: ediOrderRow.get("id") });
                    });
                }
                else
                    log.info("User chose not to compare tender [id: " + tenderId + "]");
                this.sourceEdiOrder.search({ id: ediOrderRow.get("id") });
                this.sourceViewEdiOrder.search({ id: ediOrderRow.get("id") });
            });
        }
    }

    async purposeCodeSwap(event: ClickEvent) {
        if (this.isRecordLocked()) {
            CommonDialogs.showError("Record is currently locked.  Cannot swap purpose.");
            return;
        }
        //get the purpose for the row selected
        const ediOrderRow = await new ModelEdiOrder().searchSingle({ id: this.mainDataSource?.activeRow?.get("id", null) });
        const purpose = ediOrderRow.get("purpose_type");
        const purposeSwap = new PurposeSwap();
        let allowSwap: boolean = true;

        if (purpose === "O") {//change to a change and propmpt for order ID
            const dialog = new PurposeSwapDialog({ okVisible: false, title: "Order ID for Change Tender" });

            dialog.ediOrderRow = ediOrderRow;
            dialog.addUnmountListener(() => {
                if (dialog.wasCancelled) {
                    log.info("User closed the dialog.");
                    allowSwap = false;
                }
            });
            dialog.show(ediOrderRow.get("order_id")).then(async response => {
                if (allowSwap && response) {
                    const orderTextbox = dialog.findComponentById("textOrderId") as Textbox;
                    await purposeSwap.swapOriginalToChange(ediOrderRow, orderTextbox.text).then(() => {
                        this.sourceEdiOrder.search({ id: ediOrderRow.get("id") });
                        this.sourceViewEdiOrder.search({ id: ediOrderRow.get("id") });
                    });
                }
            });

        } else if (purpose === "U") {//it's a change, change to original
            CommonDialogs.showYesNo("This will update the purpose of this tender from a change to an original.  Are you sure you want to do this?", "Change Purpose",
                {
                    titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" },
                    xVisible: false
                }).then(clickedYes => {
                    if (clickedYes) {
                        purposeSwap.swapChangeToOriginal(ediOrderRow).then(() => {
                            this.sourceEdiOrder.search({ id: ediOrderRow.get("id") });
                            this.sourceViewEdiOrder.search({ id: ediOrderRow.get("id") });
                        });
                    }
                });
        }
        this.buttonPurposeSwap.tooltip = null;
    }

    buildCustomYesNoDialog(titleText: string, prompt: string, yesLabel: string, noLabel: string, customWidth?: number): Dialog {

        const labelCaption = new Label({ text: prompt, align: HorizontalAlignment.LEFT, fontSize: "medium" });
        const panelPrompt = new Panel({ marginRight: 8, verticalAlign: VerticalAlignment.TOP, fillHeight: true, rowBreak: false });
        panelPrompt.add(labelCaption);
        const width = (customWidth > 0) ? customWidth : 900;
        const panelContainer = new Panel({ fillRow: true });
        const buttonNo = new Button({ caption: noLabel, backgroundColor: "", imageName: ImageName.x, rowBreak: false });
        buttonNo.align = HorizontalAlignment.CENTER;
        buttonNo.addClickListener(() => dialog.close(false));
        const buttonYes = new Button({ caption: yesLabel, color: "primary.reverse", backgroundColor: "primary", imageName: ImageName.check });
        buttonYes.addClickListener(() => {
            if (dialog.validateSimple())
                dialog.close(true);
        });
        const panelButtons = new Panel({ align: HorizontalAlignment.CENTER, fillRow: true });
        panelButtons.add(buttonNo);
        panelButtons.add(buttonYes);
        panelContainer.add(panelPrompt);
        panelContainer.add(panelButtons);

        const props: Partial<DialogProps> = {
            width: width,
            titleProps: { imageName: ImageName.circleX, imageColor: "primary.lighter", color: "McLeodWhite" },
            panelTitleProps: { backgroundColor: "primary.darker", color: "primary.reverse" }
        };
        const dialog = CommonDialogs.createDialog(panelContainer, { title: titleText, okVisible: false, ...props });
        return dialog;
    }

    // Can't find where this is called...
    displayPartnerName(event: DataDisplayEvent) {
        const partnerNameLabel = <Label>event.target;
        partnerNameLabel.tooltipDynamicContent = async () => {
            const row = this.sourceViewEdiOrder.activeRow;
            const partnerID = row?.get("partner_id");
            const version = row?.get("version");
            const direction = row?.get("direction");
            const batch = row?.get("gs06_group_cntlno");
            const createDate = row?.get("create_date");
            if (partnerID != null && version != null && direction != null) {
                const layout = await Layout.getLoadedLayout("lme/datafusion/EdiProfileOrderQuickInfo") as EdiProfileOrderQuickInfo;
                await layout.sourceEdiorderProfile?.search({ partner_id: partnerID, version: version, direction: direction, batch: batch, trans_type: 'T', create_date: createDate });
                return layout;
            }
            return null;
        };
    }

    public get isBrokerageLtl(): boolean {
        return this._isBrokerageLtl;
    }

    public set isBrokerageLtl(value: boolean) {
        this._isBrokerageLtl = value;
        this.tabBrltlFreightItems.visible = value;
        this.tabAccessorials.visible = value;
    }

    setCompanySettings(mode: DataSourceMode) {
        this.isBrokerageLtl = LMECompanySettings.getSingleton().isBrokerageLtl();
    }

    toggleEditFields(editable: boolean): void {
        this.textboxCustomerId.enabled = editable;
        this.textboxRevenueCodeId.enabled = editable;
        this.layoutRates.buttonRate.enabled = editable;
        this.layoutRates.checkboxLockMiles.enabled = editable;
        this.buttonUsers.enabled = editable;
        this.checkboxIntercompany.enabled = editable;
        this.switchIgnoreErrors.enabled = editable;
        this.checkboxNoDisplay.enabled = editable;
    }

    sourceBrLtlEdiOrderHdrXFgpAfterExecution(event: DataSourceExecutionEvent) {
        this.layoutHandlingReqs.populateHdrs(event.dataSource.data, true);
    }

    async compareTender(ediOrderRow: RowEdiOrder, needsReply: boolean, orderIdOverride?: string): Promise<any> {
        const orderId = orderIdOverride || ediOrderRow.get("order_id");
        if (StringUtil.isEmptyString(orderId)) {
            CommonDialogs.showError("Order ID is required to compare tender.");
            return null;
        }

        const compare = new CompareTender();
        return await compare.compareTender(ediOrderRow, needsReply, undefined, orderIdOverride).then(() => {
            this.sourceEdiOrder.search({ id: ediOrderRow.get("id") });
            this.sourceViewEdiOrder.search({ id: ediOrderRow.get("id") });
        }).catch((error) => {
            CommonDialogs.showError(EdiUtility.getCleanErrorMessage(error));
            throw error;
        });
    }

    createXref() {
        if (this.isRecordLocked()) {
            CommonDialogs.showError("Record is currently locked.  Cannot create cross reference.");
            return;
        }
        const xref = this.mainDataSource.activeRow.get("missing_xref_label");
        if ("N" == xref) {
            return;
        }
        const tenderID = this.mainDataSource.activeRow.get("id");
        const shipmentId = this.mainDataSource.activeRow.get("shipment_id");
        const layout = Layout.getLayout("lme/datafusion/DataFusionLocationCrossReference", { paddingBottom: 50 }) as DataFusionLocationCrossReference;
        layout.tenderId = tenderID;
        layout.versionId = this.mainDataSource.activeRow.get("version");
        layout.partnerId = this.mainDataSource.activeRow.get("partner_id");
        layout.shipperLocReq = getDispatchControlValue("enforce_shipper_id");
        layout.fillHeight = true;
        const saveButton = new Button({
            caption: "Save",
            align: HorizontalAlignment.RIGHT,
            color: "McLeodWhite",
            id: "buttonSave",
            onClick: async (event) => {
                saveButton.busy = true;
                await layout.saveOnClick(event)
                saveButton.busy = false;
            },
            rowBreak: false
        });
        //need to look at main datasource, get stops and pass to layout
        new SlideoutDecorator({
            layout: layout,
            title: "Location Cross Reference - Shipment ID " + shipmentId,
            fillVerticalSpace: true,
            width: window.innerWidth * 0.35,
            addlComponents: saveButton,
            doAfterSlideOut: (decorator: SlideoutDecorator) => {
                if (layout.saveButtonClicked == true) {
                    this.sourceEdiOrder.search({ id: tenderID });
                    this.sourceViewEdiOrder.search({ id: tenderID });
                }
            }
        });
    }

    async hasInvalidChargeCodes(tenderId: string, direction: string): Promise<boolean> {
        let invalidChargeCode = false;
        const otherCharges = await new ModelOtherChargeEdi().search({ order_id: tenderId, direction: direction });
        if (otherCharges) {
            for (const modelRow of otherCharges?.modelRows) {
                if (modelRow.get("charge_id") === null || modelRow.get("charge_id") === undefined) {
                    invalidChargeCode = true;
                    break;
                }
            }
        }
        return invalidChargeCode;
    }

    async replyToTender(orderId: string, ediOrderId: string, ediOrderRow: ModelRow) {
        if (!StringUtil.isEmptyString(orderId)) {
            await this.acceptLoadTender(ediOrderId, orderId).then(response => {
                if (response != null) {
                    const batch = response.data[0].reply_batch;
                    const description = response.data[0].description;

                    if (batch != "-1") {
                        Toast.showToast(this.getResultsToastPanel(orderId, description, "created"), null, { millisUntilDismissal: 7000 });
                    }
                    else {
                        const message = "Order " + orderId + " created.  Unable to send reply.  " + description;
                        Snackbar.showWarningSnackbar(message);
                    }
                }
                else {
                    const message = "Order " + orderId + " created.  Unable to send reply.";
                    Snackbar.showWarningSnackbar(message);
                }

                this.sourceViewEdiOrder.search({ id: ediOrderId });

            }).catch(error => {
                const message = "Order " + orderId + " created.  Unable to send reply.";
                Snackbar.showWarningSnackbar(message);
            });
        }
        else {
            if (ediOrderRow.get("reply_created") === "Y") {
                ediOrderRow.set("record_in_use", "N");
                ediOrderRow.post();
                return;
            }
            CommonDialogs.showYesNo("Order not created.  Would you still like to accept the tender?", "Reply to Tender",
                { titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" } }).then(async yes => {
                    if (yes) {
                        await this.acceptLoadTender(ediOrderId, orderId).then(response => {
                            if (response != null) {
                                const batch = response.data[0].reply_batch;

                                if (batch != "-1" && batch != "0") {
                                    const message = "Order not created.  Reply sent with batch " + batch + ".";
                                    Toast.showSuccessToast(message);
                                    const newRow = "N/A" + "," + ediOrderId + "," + "O" + "," + message;
                                }
                                else {
                                    const error = response.data[0].description;
                                    const message = "Order not created.  Unable to send reply.  " + error;
                                    Snackbar.showWarningSnackbar(message);
                                    const newRow = "N/A" + "," + ediOrderId + "," + "O" + "," + message;
                                }
                            }
                            else {
                                const message = "Unable to send reply.";
                                Snackbar.showWarningSnackbar(message);
                                const newRow = "N/A" + "," + ediOrderId + "," + "O" + "," + message;
                            }
                            this.refresh();
                            this.mainDataSource.mode = DataSourceMode.UPDATE;
                            this.dataHeader.showSave = false;
                        });
                    }
                    else {
                        ediOrderRow.set("record_in_use", "N");
                        ediOrderRow.post();
                    }
                }).catch(async () => {
                    ediOrderRow.set("record_in_use", "N");
                    ediOrderRow.post();
                });
        }
    }

    openRawDataSlideout(rawData: boolean, buttonClicked?: string) {
        const tenderId = this.mainDataSource?.activeRow?.get("id");
        const templateType = this.mainDataSource?.activeRow?.get("edi_template.format_type");
        const replyCreated = this.mainDataSource?.activeRow?.get("reply_created");
        EdiRawDataSlideout.openSlideout({ title: "Tender ID " + tenderId, tenderId, templateType, replyCreated, rawData, buttonClicked, fromViewEdiOrder: true });
    }

    rawDataOnClick(event: ClickEvent) {
        const buttonId: string = event.target.id;
        this.openRawDataSlideout(true, buttonId);
    }

    verifyHasCommmitment() {
        const modelRow = this.mainDataSource.activeRow;
        const buttonCommitment = this.findComponentById("imageCommitment") as Button;
        const commitmentId = modelRow.get("commitment_id");
        const origDestRateId = modelRow.get("orig_dest_rate_id");
        if (!commitmentId || !origDestRateId) buttonCommitment.visible = false;
    }

    imageCommitmentOnClick(event: ClickEvent) {
        const button = event.target as Button;
        button.busy = true;
        const row: ModelRow = this.mainDataSource.activeRow;
        this.openSuggestedPricingSlideup(event, button, row, "tabCommitmentAutoRating");
    }

    buttonSuggestedPricingOnClick(event: ClickEvent) {
        if (this.mainDataSource.activeRow == null) {
            Snackbar.showWarningSnackbar("No tender has been found to view its suggested pricing");
            return;
        }
        const button = event.target as Button;
        button.busy = true;
        const row: ModelRow = this.mainDataSource.activeRow;
        this.openSuggestedPricingSlideup(event, button, row, "tabLaneHistory");
    }

    openSuggestedPricingSlideup(event: ClickEvent, button: Button, row: ModelRow, tabId: string) {
        const tenderID = row.get("id");
        const ediSuggestedPricingLayout: EdiSuggestedPricing = Layout.getLayout("lme/datafusion/EdiSuggestedPricing") as EdiSuggestedPricing;

        const excludeButton = ediSuggestedPricingLayout.excludeButton;

        // all set to false. Might use in the future
        const setButtonsActive = (declineButtonEnabled: boolean) => {
            if (acceptButton != null) acceptButton.enabled = false;
            if (acceptButton != null) acceptButton.visible = false;
            if (declineButton != null) declineButton.enabled = false;
            if (declineButton != null) declineButton.visible = false;
            if (excludeButton != null) excludeButton.enabled = false;
            if (excludeButton != null) excludeButton.visible = false;
        }

        //****Leaving code for future use****
        // ediSuggestedPricingLayout.excludeButtonCallback = () => {
        //     ediSuggestedPricingLayout.slideOut();
        //     setTimeout(() => this.excludeTender(event), 250);
        // };

        //Button disabled but may use in the future here
        const declineButton = ediSuggestedPricingLayout.declineButton;
        ediSuggestedPricingLayout.declineButtonCallback = () => {
            ediSuggestedPricingLayout.slideOut();
            setTimeout(() => this.declineTender(event), 250);  //need logic for decline
        };
        //Button disabled but may use in the future here
        const acceptButton = ediSuggestedPricingLayout.acceptButton;
        ediSuggestedPricingLayout.acceptButtonCallback = () => {
            ediSuggestedPricingLayout.slideOut();
            setTimeout(() => this.acceptTender(this.mainDataSource.activeRow), 250);
        };

        if (row != null) {
            if (((row.get("initial_purpose") === "O" && row.get("requires_reply_original") === "Y") ||
                (row.get("initial_purpose") === "U" && row.get("requires_reply_change") === "Y") ||
                (row.get("initial_purpose") === "C" && row.get("requires_reply_cancel") === "Y"))
                && row.get("reply_created") !== "Y") {
                setButtonsActive(true);
            }
            else {
                setButtonsActive(false);
            }
        }
        ediSuggestedPricingLayout.initialSelectedTab = tabId;

        const slideout = new SlideoutDecorator({
            title: `Suggested Pricing - Tender ${tenderID}`,
            layout: ediSuggestedPricingLayout,
            layoutLoadListeners: async (event) => {
                await ediSuggestedPricingLayout.mainDataSource.search({ id: tenderID });
                ediSuggestedPricingLayout.mainDataSource.mode = DataSourceMode.NONE;
            },
            fillVerticalSpace: true,
            fillHorizontalSpace: true,
            transitionOptions: { direction: Alignment.CENTER },
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            addlComponents: [excludeButton, declineButton, acceptButton],
            onClose: (cancelled: boolean) => ediSuggestedPricingLayout.slideOut(),
            doAfterSlideIn: () => button.busy = false
        });
    }

    showCompareHistory(orderId: string, tenderId: string) {
        const layout = Layout.getLayout("lme/datafusion/CompareHistoryList", {
            width: window.innerWidth * .75,
            backgroundColor: "defaultBackground",
            fillHeight: true,
            paddingBottom: 50
        }) as CompareHistoryList;
        new SlideoutDecorator({
            layout,
            title: "Compare History: " + tenderId,
            fillVerticalSpace: true,
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                layout.textboxTargetOrderId.text = orderId;
                layout.textboxSourceRecordId.text = tenderId;
                layout.textboxTransactionType.selectedItem = new DropdownItem("T", "LOAD TENDER");
                await layout.mainDataSource.search({ "target_order_id": orderId, "source_record_id": tenderId, "transaction_type": "T" });
            }
        });
    }

    labelLabel5OnDataDisplay(event: DataDisplayEvent) {
        const label = <Label>event.target;
        label.color = "default.dark";
    }
}
